/**
 * IndexedDB Storage for Screenwriter App
 * Provides offline-first storage with sync-ready schema
 */

export const DB_NAME = 'ScreenwriterDB';
export const DB_VERSION = 26; // Bumped: added radio_play_recordings store

// Store names
const STORES = {
  FILES: 'files',
  FILE_LANGUAGES: 'file_languages', // New store for multilingual content
  FILE_VERSIONS: 'file_versions', // Git-like version history
  ANNOTATIONS: 'annotations',
  METADATA: 'metadata',
  CHAT_HISTORIES: 'chat_histories', // Renamed from chat_contexts to match server
  COLLABORATIONS: 'collaborations',
  SHARED_FILES: 'shared_files',
  PUBLIC_SHARES: 'public_shares',
  PUBLIC_USERS: 'public_users',
  AI_PROCESSING_RESULTS: 'ai_processing_results', // Individual AI processing results
  PROJECTS: 'projects', // Template organization system
  DOCUMENT_OUTLINES: 'document_outlines', // Generic outlines (beat sheets, etc.)
  FILE_METADATA: 'file_metadata', // Metadata files (story, init_context, critique)
  STANDALONE_NOTES: 'standalone_notes', // Standalone notes (Keep-style notes)
  STANDALONE_FOLDERS: 'standalone_folders', // Folders for standalone notes
  ANALYTICS_EVENTS: 'analytics_events', // User analytics and behavior tracking
  SAVE_EVENTS: 'save_events', // File save event tracking
  CHECKPOINTS: 'checkpoints', // Version-history entries flagged as checkpoints
  VERSION_MERGE_INFO: 'version_merge_info', // Commits tagged as auto-merged/manually-resolved
  RADIO_PLAY_RECORDINGS: 'radio_play_recordings' // Past "Convert to Audio" generations, per file
};

// File schema (matches your requirement minus local_id and locked)
const FILE_SCHEMA = {
  id: 'string', // primary key
  project_id: 'string',
  user_id: 'string', // kept for future sync
  name: 'string',
  created: 'date',
  last_modified: 'date',
  template_id: 'string',
  title_page: 'object', // jsonb equivalent
  content: 'string',
  pinned_locations: 'object', // jsonb equivalent
  annotations: 'object', // jsonb equivalent
  default_language: 'string', // default language code (e.g., 'en', 'es', 'fr')
  available_languages: 'object', // array of available language codes
  storageMode: 'string' // 'local' | 'cloud' - determines sync behavior (default: 'cloud')
};

// File Languages schema for multilingual content
const FILE_LANGUAGE_SCHEMA = {
  id: 'string', // primary key
  file_id: 'string', // reference to parent file
  language_code: 'string', // ISO language code (en, es, fr, etc.)
  language_name: 'string', // Human readable name (English, Spanish, French)
  content: 'string', // language-specific content
  title_page: 'object', // language-specific title page
  annotations: 'object', // language-specific annotations
  pinned_locations: 'object', // language-specific pinned locations
  last_modified: 'date',
  created: 'date'
};

// Import jobs and document chunks schemas removed (import simplified to single API call)

// Document Chunks schema for processing large documents

// AI Processing Results schema for individual element results
const AI_PROCESSING_RESULT_SCHEMA = {
  id: 'string', // primary key
  chunk_id: 'string', // reference to document chunk
  import_job_id: 'string', // reference to import job
  element_type: 'string', // scene-heading, action, character, etc.
  original_text: 'string', // original text
  processed_text: 'string', // AI-formatted text
  confidence_score: 'number', // AI confidence in formatting
  user_approved: 'boolean', // user approval status
  created_at: 'date'
};

// Projects schema for template organization system
const PROJECT_SCHEMA = {
  id: 'string', // primary key UUID
  userId: 'string', // user who owns this project
  name: 'string', // project name
  description: 'string', // optional description
  createdAt: 'date',
  updatedAt: 'date'
};

// Document Notes schema (Generic - replaces specific "character notes")
const DOCUMENT_NOTES_SCHEMA = {
  id: 'string', // primary key UUID
  projectId: 'string', // optional project reference
  fileId: 'string', // file this belongs to
  userId: 'string', // owner
  templateId: 'string', // template type (screenplay, youtube, etc.)
  noteType: 'string', // 'character', 'creator', 'hashtag', 'guest', 'cast', 'shot'
  title: 'string', // Character name, Guest name, etc.
  content: 'string', // rich text notes
  metadata: 'object', // template-specific data (JSON)
  extractedFrom: 'array', // array of element IDs this was extracted from
  createdAt: 'date',
  updatedAt: 'date'
};

// Document Outlines schema (Generic - replaces specific "beat sheets")
const DOCUMENT_OUTLINES_SCHEMA = {
  id: 'string', // primary key UUID
  projectId: 'string', // optional project reference
  fileId: 'string', // file this belongs to
  userId: 'string', // owner
  templateId: 'string', // template type
  outlineType: 'string', // 'beatsheet', 'content', 'strategy', 'episode', 'act', 'sync'
  title: 'string', // "Act 1 Beat Sheet", "Episode Outline", etc.
  content: 'string', // structured outline content
  aiGenerated: 'boolean', // whether this was AI generated
  aiPromptUsed: 'string', // which prompt template was used
  metadata: 'object', // template-specific data (JSON)
  createdAt: 'date',
  updatedAt: 'date'
};

// Chat Histories schema (matches server schema from chat-history.js)
// eslint-disable-next-line no-unused-vars
const CHAT_HISTORIES_SCHEMA = {
  id: 'string', // primary key UUID
  server_id: 'string', // server-side chat history ID (for tracking synced records)
  userId: 'string', // user who owns this chat
  sessionId: 'string', // chat session identifier
  fileId: 'string', // file this chat message is associated with (NEW: separate column)
  messageType: 'string', // 'user_input', 'ai_response', etc.
  content: 'string', // message content
  mode: 'string', // 'chat', 'format', etc.
  messageMode: 'string', // message-specific mode (ask, plan, format) for proper display
  template: 'string', // template being used (optional)
  category: 'string', // template category (screenplay, book, shortform, production) for mode filtering
  formattedElements: 'object', // formatted content elements (JSON)
  originalText: 'string', // original selected text (optional)
  contextData: 'object', // additional context data (JSON)
  messageContext: 'object', // message-specific context (JSON)
  tokensUsed: 'number', // tokens consumed in this message (legacy - use tokens_input + tokens_output)
  tokens_input: 'number', // input tokens consumed
  tokens_output: 'number', // output tokens consumed
  cost_usd: 'number', // cost in USD
  synced: 'boolean', // whether this message has been synced to server
  model_used: 'string', // AI model used for this message
  sessionMetadata: 'object', // session-level metadata (template, contentType, genre, length, platform, etc.)
  createdAt: 'date'
};

// Standalone Notes schema (decoupled from documents/templates)
// eslint-disable-next-line no-unused-vars
const STANDALONE_NOTES_SCHEMA = {
  id: 'string', // primary key UUID
  title: 'string', // Note title
  content: 'string', // Rich HTML content
  color: 'string', // Background color (#hex)
  createdAt: 'date',
  updatedAt: 'date',
  metadata: 'object', // { pinned: boolean, noteType: 'text'|'list', folderId: string|null }
  // Sync tracking fields for offline-first architecture
  syncStatus: 'string', // 'synced' | 'pending' | 'error'
  lastSyncedAt: 'date', // When last synced to server
  serverUpdatedAt: 'date' // Server's updatedAt timestamp
};

// Standalone Folders schema (for organizing standalone notes)
// eslint-disable-next-line no-unused-vars
const STANDALONE_FOLDERS_SCHEMA = {
  id: 'string', // primary key UUID
  name: 'string', // Folder name
  createdAt: 'date',
  // Sync tracking fields for offline-first architecture
  syncStatus: 'string', // 'synced' | 'pending' | 'error'
  lastSyncedAt: 'date' // When last synced to server
};

class ScreenwriterDB {
  constructor() {
    this.db = null;
    this._initPromise = null;
    this.currentUserId = this.getUserId();
  }

  // Get user ID from localStorage (should be set after login)
  getUserId() {
    // First, try to get user ID from currentUser (set by authService)
    try {
      const currentUserData = localStorage.getItem('currentUser');
      if (currentUserData) {
        const currentUser = JSON.parse(currentUserData);
        if (currentUser && currentUser.id) {
          // Sync with screenwriter_user_id for backwards compatibility
          localStorage.setItem('screenwriter_user_id', currentUser.id);
          return currentUser.id;
        }
      }
    } catch (error) {
      console.error('Error parsing currentUser from localStorage:', error);
    }

    // Fallback to screenwriter_user_id
    let userId = localStorage.getItem('screenwriter_user_id');
    if (!userId) {
      // Generate temporary local ID for offline use
      // This will be replaced with server-generated UUID after login/registration
      userId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('screenwriter_user_id', userId);
      console.warn('Using temporary local user ID. Please log in to sync with server.');
    }
    return userId;
  }

  // Set user ID after successful authentication
  setUserId(userId) {
    if (userId) {
      localStorage.setItem('screenwriter_user_id', userId);
      this.currentUserId = userId;
      console.log('User ID set:', userId);
    }
  }

  // Re-read the latest userId from localStorage and sync if it changed.
  // Handles the case where the singleton was created before login completed.
  _syncUserId() {
    try {
      const fresh = this.getUserId();
      if (fresh && fresh !== this.currentUserId) {
        localStorage.setItem('screenwriter_user_id', fresh);
        this.currentUserId = fresh;
      }
    } catch (_) {}
  }

  // Initialize database (race-safe: concurrent callers share the same promise)
  async init() {
    if (this.db) return this.db;
    if (this._initPromise) return this._initPromise;

    console.log('IndexedDB init() called');
    this._initPromise = this._openDatabase();
    return this._initPromise;
  }

  // Opens the database, with one automatic recovery attempt if the browser's
  // IndexedDB storage for this origin has become corrupted. A corrupted
  // origin surfaces as a generic UnknownError (not the specific VersionError
  // a schema mismatch would throw), and never resolves on its own — without
  // this, every returning user hitting it stays permanently broken until
  // they manually clear site data. allowRecovery=false on the retry so we
  // only ever attempt one wipe-and-recreate, never loop.
  _openDatabase(allowRecovery = true) {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = async () => {
        const error = request.error;
        console.error('IndexedDB error:', error);
        this._initPromise = null; // Allow retry on failure

        if (allowRecovery && error?.name === 'UnknownError') {
          console.warn('IndexedDB storage appears corrupted — deleting and recreating local database...');
          try {
            await this.deleteDatabase();
            const recovered = await this._openDatabase(false);
            this.db = recovered;
            this._initPromise = Promise.resolve(recovered);
            resolve(recovered);
            return;
          } catch (recoveryError) {
            console.error('IndexedDB recovery failed:', recoveryError);
            reject(recoveryError);
            return;
          }
        }

        reject(error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB initialized successfully, database:', !!this.db);
        console.log('User ID:', this.currentUserId);
        // One-time migration: clear stale chat_histories now that chat reads from server
        if (!localStorage.getItem('chat_idb_cleared_v1')) {
          try {
            const tx = this.db.transaction(['chat_histories'], 'readwrite');
            tx.objectStore('chat_histories').clear();
            tx.oncomplete = () => {
              localStorage.setItem('chat_idb_cleared_v1', '1');
              console.log('Cleared chat_histories from IndexedDB (migrated to server-only)');
            };
          } catch (_) {}
        }
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        console.log('IndexedDB upgrade needed, creating stores...');
        const db = event.target.result;
        this.createStores(db, event);
        console.log('IndexedDB stores created');
      };
    });
  }

  // Delete database (for handling version conflicts)
  async deleteDatabase() {
    return new Promise((resolve, reject) => {
      if (this.db) {
        this.db.close();
        this.db = null;
      }
      this._initPromise = null;

      const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
      deleteRequest.onerror = () => reject(deleteRequest.error);
      deleteRequest.onsuccess = () => {
        console.log('Database deleted successfully');
        resolve();
      };
      deleteRequest.onblocked = () => {
        console.log('Database deletion blocked, retrying...');
        setTimeout(() => resolve(), 100);
      };
    });
  }

  // Create object stores
  createStores(db, event = null) {
    // Files store
    if (!db.objectStoreNames.contains(STORES.FILES)) {
      const filesStore = db.createObjectStore(STORES.FILES, { keyPath: 'id' });
      filesStore.createIndex('user_id', 'user_id', { unique: false });
      filesStore.createIndex('project_id', 'project_id', { unique: false });
      filesStore.createIndex('last_modified', 'last_modified', { unique: false });
      filesStore.createIndex('name', 'name', { unique: false });
      filesStore.createIndex('default_language', 'default_language', { unique: false });
    }

    // File Languages store for multilingual content
    if (!db.objectStoreNames.contains(STORES.FILE_LANGUAGES)) {
      const fileLanguagesStore = db.createObjectStore(STORES.FILE_LANGUAGES, { keyPath: 'id' });
      fileLanguagesStore.createIndex('file_id', 'file_id', { unique: false });
      fileLanguagesStore.createIndex('language_code', 'language_code', { unique: false });
      fileLanguagesStore.createIndex('file_language', ['file_id', 'language_code'], { unique: true });
      fileLanguagesStore.createIndex('last_modified', 'last_modified', { unique: false });
    }

    // File Versions store for git-like version control
    if (!db.objectStoreNames.contains(STORES.FILE_VERSIONS)) {
      const fileVersionsStore = db.createObjectStore(STORES.FILE_VERSIONS, { keyPath: 'id' });
      fileVersionsStore.createIndex('file_id', 'file_id', { unique: false });
      fileVersionsStore.createIndex('version', 'version', { unique: false });
      fileVersionsStore.createIndex('file_version_lang', ['file_id', 'version', 'language_code'], { unique: true });
      fileVersionsStore.createIndex('created_at', 'created_at', { unique: false });
      fileVersionsStore.createIndex('author_id', 'author_id', { unique: false });
      fileVersionsStore.createIndex('needs_sync', 'needs_sync', { unique: false });
    }

    // Annotations store (separate for better performance)
    if (!db.objectStoreNames.contains(STORES.ANNOTATIONS)) {
      const annotationsStore = db.createObjectStore(STORES.ANNOTATIONS, { keyPath: 'id' });
      annotationsStore.createIndex('file_id', 'file_id', { unique: false });
      annotationsStore.createIndex('user_id', 'user_id', { unique: false });
      annotationsStore.createIndex('element_id', 'element_id', { unique: false });
      annotationsStore.createIndex('language_code', 'language_code', { unique: false });
    }

    // Metadata store (app-level data)
    if (!db.objectStoreNames.contains(STORES.METADATA)) {
      db.createObjectStore(STORES.METADATA, { keyPath: 'key' });
    }

    // Chat histories store (renamed from chat_contexts)
    if (!db.objectStoreNames.contains(STORES.CHAT_HISTORIES)) {
      const chatHistoriesStore = db.createObjectStore(STORES.CHAT_HISTORIES, { keyPath: 'id' });
      chatHistoriesStore.createIndex('userId', 'userId', { unique: false });
      chatHistoriesStore.createIndex('sessionId', 'sessionId', { unique: false });
      chatHistoriesStore.createIndex('createdAt', 'createdAt', { unique: false });
      chatHistoriesStore.createIndex('synced', 'synced', { unique: false });
      chatHistoriesStore.createIndex('server_id', 'server_id', { unique: false });
    } else if (event && event.oldVersion < 11) {
      // Migration: Add synced and server_id indexes to existing chat_histories store
      const transaction = event.target.transaction;
      const chatHistoriesStore = transaction.objectStore(STORES.CHAT_HISTORIES);

      // Add synced index if it doesn't exist
      if (!chatHistoriesStore.indexNames.contains('synced')) {
        chatHistoriesStore.createIndex('synced', 'synced', { unique: false });
      }

      // Add server_id index if it doesn't exist
      if (!chatHistoriesStore.indexNames.contains('server_id')) {
        chatHistoriesStore.createIndex('server_id', 'server_id', { unique: false });
      }
    }

    // Collaborations store
    if (!db.objectStoreNames.contains(STORES.COLLABORATIONS)) {
      const collaborationsStore = db.createObjectStore(STORES.COLLABORATIONS, { keyPath: 'id' });
      collaborationsStore.createIndex('file_id', 'file_id', { unique: false });
      collaborationsStore.createIndex('user_email', 'user_email', { unique: false });
      collaborationsStore.createIndex('permission_level', 'permission_level', { unique: false });
      collaborationsStore.createIndex('created_by', 'created_by', { unique: false });
    }

    // Shared files store (tracks files shared with current user)
    if (!db.objectStoreNames.contains(STORES.SHARED_FILES)) {
      const sharedFilesStore = db.createObjectStore(STORES.SHARED_FILES, { keyPath: 'id' });
      sharedFilesStore.createIndex('file_id', 'file_id', { unique: false });
      sharedFilesStore.createIndex('owner_user_id', 'owner_user_id', { unique: false });
      sharedFilesStore.createIndex('shared_with_user_id', 'shared_with_user_id', { unique: false });
      sharedFilesStore.createIndex('permission_level', 'permission_level', { unique: false });
      sharedFilesStore.createIndex('shared_date', 'shared_date', { unique: false });
    }

    // Public shares store (tracks publicly shared files)
    if (!db.objectStoreNames.contains(STORES.PUBLIC_SHARES)) {
      const publicSharesStore = db.createObjectStore(STORES.PUBLIC_SHARES, { keyPath: 'id' });
      publicSharesStore.createIndex('file_id', 'file_id', { unique: false });
      publicSharesStore.createIndex('share_token', 'share_token', { unique: true });
      publicSharesStore.createIndex('owner_user_id', 'owner_user_id', { unique: false });
      publicSharesStore.createIndex('created', 'created', { unique: false });
      publicSharesStore.createIndex('is_active', 'is_active', { unique: false });
    }

    // Public users store (tracks users who accessed public shares)
    if (!db.objectStoreNames.contains(STORES.PUBLIC_USERS)) {
      const publicUsersStore = db.createObjectStore(STORES.PUBLIC_USERS, { keyPath: 'id' });
      publicUsersStore.createIndex('public_share_id', 'public_share_id', { unique: false });
      publicUsersStore.createIndex('email', 'email', { unique: false });
      publicUsersStore.createIndex('access_date', 'access_date', { unique: false });
      publicUsersStore.createIndex('signed_nda', 'signed_nda', { unique: false });
    }




    // Projects store for template organization system
    if (!db.objectStoreNames.contains(STORES.PROJECTS)) {
      const projectsStore = db.createObjectStore(STORES.PROJECTS, { keyPath: 'id' });
      projectsStore.createIndex('userId', 'userId', { unique: false });
      projectsStore.createIndex('createdAt', 'createdAt', { unique: false });
      projectsStore.createIndex('updatedAt', 'updatedAt', { unique: false });
    }

    // Document outlines store (generic outlines for all template types)
    if (!db.objectStoreNames.contains(STORES.DOCUMENT_OUTLINES)) {
      const documentOutlinesStore = db.createObjectStore(STORES.DOCUMENT_OUTLINES, { keyPath: 'id' });
      documentOutlinesStore.createIndex('projectId', 'projectId', { unique: false });
      documentOutlinesStore.createIndex('fileId', 'fileId', { unique: false });
      documentOutlinesStore.createIndex('userId', 'userId', { unique: false });
      documentOutlinesStore.createIndex('templateId', 'templateId', { unique: false });
      documentOutlinesStore.createIndex('outlineType', 'outlineType', { unique: false });
      documentOutlinesStore.createIndex('file_template', ['fileId', 'templateId'], { unique: false });
      documentOutlinesStore.createIndex('aiGenerated', 'aiGenerated', { unique: false });
      documentOutlinesStore.createIndex('createdAt', 'createdAt', { unique: false });
      documentOutlinesStore.createIndex('updatedAt', 'updatedAt', { unique: false });
    }

    // File metadata store (story, init_context, critique as metadata files)
    if (!db.objectStoreNames.contains(STORES.FILE_METADATA)) {
      const fileMetadataStore = db.createObjectStore(STORES.FILE_METADATA, { keyPath: 'id' });
      fileMetadataStore.createIndex('fileId', 'fileId', { unique: false });
      fileMetadataStore.createIndex('userId', 'userId', { unique: false });
      fileMetadataStore.createIndex('metadataType', 'metadataType', { unique: false });
      fileMetadataStore.createIndex('file_metadataType', ['fileId', 'metadataType'], { unique: true });
      fileMetadataStore.createIndex('createdAt', 'createdAt', { unique: false });
      fileMetadataStore.createIndex('updatedAt', 'updatedAt', { unique: false });
    }

    // Standalone Notes store (decoupled Google Keep-style notes)
    if (!db.objectStoreNames.contains(STORES.STANDALONE_NOTES)) {
      const standaloneNotesStore = db.createObjectStore(STORES.STANDALONE_NOTES, { keyPath: 'id' });
      standaloneNotesStore.createIndex('createdAt', 'createdAt', { unique: false });
      standaloneNotesStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      standaloneNotesStore.createIndex('folderId', 'metadata.folderId', { unique: false });
      standaloneNotesStore.createIndex('pinned', 'metadata.pinned', { unique: false });
    }

    // Standalone Folders store (for organizing standalone notes)
    if (!db.objectStoreNames.contains(STORES.STANDALONE_FOLDERS)) {
      const standaloneFoldersStore = db.createObjectStore(STORES.STANDALONE_FOLDERS, { keyPath: 'id' });
      standaloneFoldersStore.createIndex('createdAt', 'createdAt', { unique: false });
      standaloneFoldersStore.createIndex('name', 'name', { unique: false });
    }

    // Analytics Events store (user analytics and behavior tracking)
    if (!db.objectStoreNames.contains(STORES.ANALYTICS_EVENTS)) {
      const analyticsEventsStore = db.createObjectStore(STORES.ANALYTICS_EVENTS, { keyPath: 'id' });
      analyticsEventsStore.createIndex('userId', 'userId', { unique: false });
      analyticsEventsStore.createIndex('sessionId', 'sessionId', { unique: false });
      analyticsEventsStore.createIndex('eventType', 'eventType', { unique: false });
      analyticsEventsStore.createIndex('eventName', 'eventName', { unique: false });
      analyticsEventsStore.createIndex('createdAt', 'createdAt', { unique: false });
      analyticsEventsStore.createIndex('synced', 'synced', { unique: false });
      analyticsEventsStore.createIndex('retryCount', 'retryCount', { unique: false });
    }

    // Save Events store (file save event tracking)
    if (!db.objectStoreNames.contains(STORES.SAVE_EVENTS)) {
      const saveEventsStore = db.createObjectStore(STORES.SAVE_EVENTS, { keyPath: 'id' });
      saveEventsStore.createIndex('userId', 'userId', { unique: false });
      saveEventsStore.createIndex('fileId', 'fileId', { unique: false });
      saveEventsStore.createIndex('saveType', 'saveType', { unique: false });
      saveEventsStore.createIndex('success', 'success', { unique: false });
      saveEventsStore.createIndex('createdAt', 'createdAt', { unique: false });
      saveEventsStore.createIndex('synced', 'synced', { unique: false });
      saveEventsStore.createIndex('retryCount', 'retryCount', { unique: false });
    }

    // Checkpoints store (version-history entries flagged as checkpoints)
    if (!db.objectStoreNames.contains(STORES.CHECKPOINTS)) {
      const checkpointsStore = db.createObjectStore(STORES.CHECKPOINTS, { keyPath: 'id' });
      checkpointsStore.createIndex('file_id', 'file_id', { unique: false });
      checkpointsStore.createIndex('file_commit', ['file_id', 'commit_sha'], { unique: true });
      checkpointsStore.createIndex('synced', 'synced', { unique: false });
    }

    // Version merge info store (commits tagged as auto-merged/manually-resolved)
    if (!db.objectStoreNames.contains(STORES.VERSION_MERGE_INFO)) {
      const mergeInfoStore = db.createObjectStore(STORES.VERSION_MERGE_INFO, { keyPath: 'id' });
      mergeInfoStore.createIndex('file_id', 'file_id', { unique: false });
      mergeInfoStore.createIndex('file_commit_lang', ['file_id', 'commit_sha', 'language_code'], { unique: true });
      mergeInfoStore.createIndex('synced', 'synced', { unique: false });
    }

    // Radio play recordings store (past "Create Audiobook" generations, per file)
    if (!db.objectStoreNames.contains(STORES.RADIO_PLAY_RECORDINGS)) {
      const radioPlayRecordingsStore = db.createObjectStore(STORES.RADIO_PLAY_RECORDINGS, { keyPath: 'id' });
      radioPlayRecordingsStore.createIndex('file_id', 'file_id', { unique: false });
      radioPlayRecordingsStore.createIndex('created_at', 'created_at', { unique: false });
    }

    // Sessions store (AI sessions) - Added for compatibility with optimized utils
    if (!db.objectStoreNames.contains('sessions')) {
      const sessionsStore = db.createObjectStore('sessions', { keyPath: 'id' });
      sessionsStore.createIndex('user_id', 'user_id', { unique: false });
      sessionsStore.createIndex('agent_type', 'agent_type', { unique: false });
      sessionsStore.createIndex('status', 'status', { unique: false });
      sessionsStore.createIndex('created_at', 'created_at', { unique: false });
    }

    // Cache store (TTL cache) - Added for compatibility with optimized utils
    if (!db.objectStoreNames.contains('cache')) {
      const cacheStore = db.createObjectStore('cache', { keyPath: 'key' });
      cacheStore.createIndex('expires_at', 'expires_at', { unique: false });
    }

    // --------------------------------------------------------
    // MIGRATIONS (Versioning Logic)
    // --------------------------------------------------------
    if (event) {
      const transaction = event.target.transaction;
      
      // Upgrade logic for 'files' store indexes
      if (db.objectStoreNames.contains(STORES.FILES)) {
        const filesStore = transaction.objectStore(STORES.FILES);
        
        // Ensure all indexes required by optimized utils exist
        if (!filesStore.indexNames.contains('template_id')) filesStore.createIndex('template_id', 'template_id', { unique: false });
        if (!filesStore.indexNames.contains('created_at')) filesStore.createIndex('created_at', 'created_at', { unique: false }); // Map to 'created'
        if (!filesStore.indexNames.contains('updated_at')) filesStore.createIndex('updated_at', 'updated_at', { unique: false }); // Map to 'last_modified'
        if (!filesStore.indexNames.contains('user_template')) filesStore.createIndex('user_template', ['user_id', 'template_id'], { unique: false });
        
        // Ensure standard indexes exist
        if (!filesStore.indexNames.contains('user_id')) filesStore.createIndex('user_id', 'user_id', { unique: false });
        if (!filesStore.indexNames.contains('project_id')) filesStore.createIndex('project_id', 'project_id', { unique: false });
        if (!filesStore.indexNames.contains('last_modified')) filesStore.createIndex('last_modified', 'last_modified', { unique: false });
        if (!filesStore.indexNames.contains('name')) filesStore.createIndex('name', 'name', { unique: false });
        if (!filesStore.indexNames.contains('default_language')) filesStore.createIndex('default_language', 'default_language', { unique: false });
      }
    }
  }

  // Generic transaction helper
  async transaction(storeNames, mode = 'readonly') {
    if (!this.db) {
      throw new Error('Database not initialized');
    }
    return this.db.transaction(storeNames, mode);
  }

  // Generate unique ID - using UUID v4 format for server compatibility
  generateId() {
    // Generate UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  }

  // Get language name from code
  getLanguageName(code) {
    const languages = {
      'en': 'English',
      'es': 'Español',
      'fr': 'Français',
      'de': 'Deutsch',
      'it': 'Italiano',
      'pt': 'Português',
      'ru': 'Русский',
      'ja': '日本語',
      'ko': '한국어',
      'zh': '中文',
      'ar': 'العربية',
      'hi': 'हिन्दी'
    };
    return languages[code] || code.toUpperCase();
  }

  // Get supported languages list
  getSupportedLanguages() {
    const languageList = require('language-list');
    const allLanguages = languageList().getData();

    // Map to the expected format (code, name)
    return allLanguages.map(lang => ({
      code: lang.code,
      name: lang.language
    }));
  }

  // FILES OPERATIONS

  // Create new file
  async createFile(fileData) {
    // ✅ FIXED: Extract content separately - it should ONLY go to file_languages table
    const initialContent = fileData.content || '';

    const file = {
      id: fileData.id || this.generateId(), // Use provided ID if available
      project_id: fileData.project_id || null, // Don't auto-generate, let caller decide
      user_id: this.currentUserId,
      name: fileData.name || 'Untitled',
      created: fileData.created || new Date(),
      last_modified: fileData.last_modified || new Date(),
      template_id: fileData.template_id || fileData.templateId || 'screenplay',
      title_page: fileData.title_page || {},
      content: '', // ✅ FIXED: Always empty - content stored ONLY in file_languages table
      pinned_locations: fileData.pinned_locations || {},
      annotations: fileData.annotations || {},
      default_language: fileData.default_language || 'en',
      available_languages: fileData.available_languages || ['en'],
      storageMode: fileData.storageMode || 'cloud', // 'local' | 'cloud' - determines sync behavior
      // Sync metadata -- ONLY carried through when the caller explicitly provides it
      // (never defaulted). syncService.js's downloadFileFromServer/applyServerChange
      // pass last_synced/last_synced_at when creating a brand-new local row FROM a
      // server record (via upsertFile -> createFile, since the file doesn't exist
      // locally yet) -- without this, those fields were silently dropped here because
      // this whitelist didn't include them, so a file that just came straight from the
      // server (and is by definition in sync at that instant) permanently evaluated as
      // "not synced" in the sidebar until some later update happened to touch it via
      // updateFile() instead. A genuinely new, not-yet-uploaded local file never passes
      // these fields, so it correctly still starts out with no last_synced at all.
      ...(fileData.last_synced !== undefined && { last_synced: fileData.last_synced }),
      ...(fileData.last_synced_at !== undefined && { last_synced_at: fileData.last_synced_at })
    };

    console.log('IndexedDB: Creating file with template_id:', file.template_id, '- Content will be stored in file_languages table only');

    const tx = await this.transaction([STORES.FILES, STORES.FILE_LANGUAGES], 'readwrite');
    const filesStore = tx.objectStore(STORES.FILES);
    const languagesStore = tx.objectStore(STORES.FILE_LANGUAGES);

    return new Promise((resolve, reject) => {
      const fileRequest = filesStore.add(file);
      fileRequest.onsuccess = () => {
        // Create default language entry with the actual content
        const languageEntry = {
          id: this.generateId(),
          file_id: file.id,
          language_code: file.default_language,
          language_name: this.getLanguageName(file.default_language),
          content: initialContent, // ✅ FIXED: Use extracted content, not file.content
          title_page: file.title_page,
          annotations: file.annotations,
          created: new Date(),
          last_modified: new Date()
        };

        const langRequest = languagesStore.add(languageEntry);
        langRequest.onsuccess = async () => {
          console.log('IndexedDB: File created successfully with template_id:', file.template_id);

          resolve(file);
        };
        langRequest.onerror = () => reject(langRequest.error);
      };
      fileRequest.onerror = () => reject(fileRequest.error);
    });
  }

  // Get file by ID
  async getFile(fileId) {
    const tx = await this.transaction([STORES.FILES]);
    const store = tx.objectStore(STORES.FILES);

    return new Promise((resolve, reject) => {
      const request = store.get(fileId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Update file
  async updateFile(fileId, updates) {
    const file = await this.getFile(fileId);
    if (!file) {
      throw new Error('File not found');
    }

    const updatedFile = {
      ...file,
      ...updates,
      // Only auto-update last_modified if not explicitly provided
      last_modified: updates.last_modified || new Date()
    };

    const tx = await this.transaction([STORES.FILES], 'readwrite');
    const store = tx.objectStore(STORES.FILES);

    return new Promise((resolve, reject) => {
      const request = store.put(updatedFile);
      request.onsuccess = () => resolve(updatedFile);
      request.onerror = () => reject(request.error);
    });
  }

  // Upsert file (create or update)
  async upsertFile(fileData) {
    const existingFile = await this.getFile(fileData.id);

    if (existingFile) {
      // File exists, update it
      console.log('IndexedDB: File exists, updating:', fileData.name);
      return await this.updateFile(fileData.id, fileData);
    } else {
      // File doesn't exist, create it
      console.log('IndexedDB: File does not exist, creating:', fileData.name);
      return await this.createFile(fileData);
    }
  }

  // Delete file
  async deleteFile(fileId) {
    const tx = await this.transaction([STORES.FILES], 'readwrite');
    const store = tx.objectStore(STORES.FILES);

    return new Promise((resolve, reject) => {
      const request = store.delete(fileId);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // List files for current user
  async listFiles(projectId = null) {
    console.log('listFiles called, projectId:', projectId);
    console.log('Database initialized:', !!this.db);
    this._syncUserId();
    console.log('Current user ID:', this.currentUserId);

    if (!this.db) {
      console.warn('Database not initialized in listFiles - returning empty array');
      return [];
    }


    try {
      const tx = await this.transaction([STORES.FILES]);
      const store = tx.objectStore(STORES.FILES);
      const index = store.index('user_id');

      return new Promise((resolve, reject) => {
        const files = [];
        const request = index.openCursor(this.currentUserId);

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            const file = cursor.value;
            if (!projectId || file.project_id === projectId) {
              files.push(file);
            }
            cursor.continue();
          } else {
            // Sort by last_modified descending
            files.sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified));
            console.log('listFiles returning:', files.length, 'files');
            resolve(files);
          }
        };

        request.onerror = () => {
          console.error('listFiles cursor error:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('listFiles error:', error);
      return [];
    }
  }

  // ANNOTATION OPERATIONS

  // Save annotation (with language support)
  async saveAnnotation(fileId, elementId, annotationData, languageCode = null) {
    // Get the current language if not provided
    if (!languageCode) {
      const file = await this.getFile(fileId);
      languageCode = file?.default_language || 'en';
    }

    const annotation = {
      id: this.generateId(),
      file_id: fileId,
      language_code: languageCode, // Add language code for language-specific annotations
      user_id: this.currentUserId,
      user_name: annotationData.user_name || window.currentUser?.name || 'Anonymous',
      element_id: elementId,
      type: annotationData.type, // 'voice' or 'note'
      content: annotationData.content,
      audio_data: annotationData.audio_data || null,
      duration: annotationData.duration || null,
      created: new Date(),
      last_modified: new Date()
    };

    const tx = await this.transaction([STORES.ANNOTATIONS], 'readwrite');
    const annotationStore = tx.objectStore(STORES.ANNOTATIONS);

    return new Promise((resolve, reject) => {
      const annotationRequest = annotationStore.add(annotation);
      annotationRequest.onsuccess = async () => {
        console.log(`Annotation saved for language ${languageCode}:`, annotation.id);

        // Also add annotation to the language data for sync
        try {
          await this.addAnnotationToCurrentLanguage(fileId, elementId, annotation);
          console.log(`Annotation also added to language data for ${languageCode}`);
        } catch (error) {
          console.warn('Could not add annotation to language data:', error);
        }

        resolve(annotation);
      };
      annotationRequest.onerror = () => reject(annotationRequest.error);
    });
  }

  // Add annotation (for sync service compatibility) - with language support
  async addAnnotation(annotationData) {
    const annotation = {
      id: annotationData.id || this.generateId(),
      file_id: annotationData.file_id,
      language_code: annotationData.language_code || 'en', // Add language code
      user_id: annotationData.user_id || this.currentUserId,
      user_name: annotationData.user_name || window.currentUser?.name || 'Anonymous',
      element_id: annotationData.element_id,
      type: annotationData.type,
      content: annotationData.content,
      audio_data: annotationData.audio_data || null,
      duration: annotationData.duration || null,
      server_id: annotationData.server_id || null,
      sync_status: annotationData.sync_status || 'synced',
      last_synced_at: annotationData.last_synced_at || new Date(),
      created: annotationData.created || new Date(),
      last_modified: annotationData.last_modified || new Date()
    };

    const tx = await this.transaction([STORES.ANNOTATIONS], 'readwrite');
    const annotationStore = tx.objectStore(STORES.ANNOTATIONS);

    return new Promise((resolve, reject) => {
      const annotationRequest = annotationStore.add(annotation);
      annotationRequest.onsuccess = () => {
        console.log('Annotation added:', annotation.id);
        resolve(annotation);
      };
      annotationRequest.onerror = () => reject(annotationRequest.error);
    });
  }

  // Helper function to add annotation to current language's annotations
  async addAnnotationToCurrentLanguage(fileId, elementId, annotation) {
    try {
      // Use the language from the annotation, or get the default language
      const languageCode = annotation.language_code || 'en';

      // Create a new transaction for language operations
      const tx = await this.transaction([STORES.FILE_LANGUAGES], 'readwrite');
      const languageStore = tx.objectStore(STORES.FILE_LANGUAGES);

      // Get the current language entry
      const languageIndex = languageStore.index('file_language');
      const languageEntry = await new Promise((resolve, reject) => {
        const request = languageIndex.get([fileId, languageCode]);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      if (languageEntry) {
        // Add annotation to the language's annotations object
        if (!languageEntry.annotations) {
          languageEntry.annotations = {};
        }
        if (!languageEntry.annotations[elementId]) {
          languageEntry.annotations[elementId] = [];
        }

        // Add this annotation to the element's annotations
        languageEntry.annotations[elementId].push({
          id: annotation.id,
          type: annotation.type,
          content: annotation.content,
          audio_data: annotation.audio_data,
          duration: annotation.duration,
          created: annotation.created,
          user_name: annotation.user_name
        });

        languageEntry.last_modified = new Date();

        // Save the updated language entry
        await new Promise((resolve, reject) => {
          const updateRequest = languageStore.put(languageEntry);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        });
      }
    } catch (error) {
      console.error('Error in addAnnotationToCurrentLanguage:', error);
      throw error;
    }
  }

  // Helper function to remove annotation from current language's annotations
  async removeAnnotationFromCurrentLanguage(fileId, elementId, annotationId) {
    try {
      // Get the file to find the current/default language
      const file = await this.getFile(fileId);
      if (!file) return;

      const languageCode = file.default_language || 'en';

      // Create a new transaction for language operations
      const tx = await this.transaction([STORES.FILE_LANGUAGES], 'readwrite');
      const languageStore = tx.objectStore(STORES.FILE_LANGUAGES);

      // Get the current language entry
      const languageIndex = languageStore.index('file_language');
      const languageEntry = await new Promise((resolve, reject) => {
        const request = languageIndex.get([fileId, languageCode]);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      if (languageEntry && languageEntry.annotations && languageEntry.annotations[elementId]) {
        // Remove the annotation from the element's annotations array
        const annotations = languageEntry.annotations[elementId];
        const updatedAnnotations = annotations.filter(a => a.id !== annotationId);

        if (updatedAnnotations.length === 0) {
          // Remove the entire element entry if no annotations left
          delete languageEntry.annotations[elementId];
        } else {
          // Update with filtered annotations
          languageEntry.annotations[elementId] = updatedAnnotations;
        }

        languageEntry.last_modified = new Date();

        // Save the updated language entry
        await new Promise((resolve, reject) => {
          const updateRequest = languageStore.put(languageEntry);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        });

        console.log(`Removed annotation ${annotationId} from language data for ${languageCode}`);
      }
    } catch (error) {
      console.error('Error in removeAnnotationFromCurrentLanguage:', error);
      throw error;
    }
  }

  // Update existing annotation
  async updateAnnotation(annotationId, updates) {
    const tx = await this.transaction([STORES.ANNOTATIONS, STORES.FILE_LANGUAGES, STORES.FILES], 'readwrite');
    const annotationStore = tx.objectStore(STORES.ANNOTATIONS);
    const languageStore = tx.objectStore(STORES.FILE_LANGUAGES);

    return new Promise((resolve, reject) => {
      const getRequest = annotationStore.get(annotationId);
      getRequest.onsuccess = () => {
        const annotation = getRequest.result;
        if (annotation) {
          // Update the annotation with new data
          Object.assign(annotation, updates);
          annotation.last_modified = new Date();

          const putRequest = annotationStore.put(annotation);
          putRequest.onsuccess = () => {
            try {
              // Also update in language-specific storage (synchronously within the transaction)
              this.updateAnnotationInLanguageSync(annotation.file_id, annotation.element_id, annotation, languageStore)
                .then(() => resolve(annotation))
                .catch((error) => {
                  console.warn('Failed to update annotation in language-specific storage:', error);
                  resolve(annotation); // Still resolve since main annotation was updated
                });
            } catch (error) {
              console.warn('Failed to update annotation in language-specific storage:', error);
              resolve(annotation); // Still resolve since main annotation was updated
            }
          };
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          reject(new Error('Annotation not found'));
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // Helper function to update annotation in current language's annotations (within existing transaction)
  async updateAnnotationInLanguageSync(fileId, elementId, annotation, languageStore) {
    const filesStore = languageStore.transaction.objectStore(STORES.FILES);

    return new Promise((resolve, reject) => {
      // Get file to determine current language
      const fileRequest = filesStore.get(fileId);
      fileRequest.onsuccess = () => {
        const file = fileRequest.result;
        if (!file) {
          resolve(); // File not found, skip language update
          return;
        }

        const currentLanguage = file.default_language || 'en';
        const languageIndex = languageStore.index('file_language');
        const languageRequest = languageIndex.get([fileId, currentLanguage]);

        languageRequest.onsuccess = () => {
          const languageEntry = languageRequest.result;
          if (languageEntry && languageEntry.annotations && languageEntry.annotations[elementId]) {
            // Find and update the annotation
            const annotations = languageEntry.annotations[elementId];
            const index = annotations.findIndex(a => a.id === annotation.id);
            if (index !== -1) {
              annotations[index] = {
                id: annotation.id,
                type: annotation.type,
                content: annotation.content,
                audio_data: annotation.audio_data,
                duration: annotation.duration,
                created: annotation.created,
                user_name: annotation.user_name
              };

              languageEntry.last_modified = new Date();
              const updateRequest = languageStore.put(languageEntry);
              updateRequest.onsuccess = () => resolve();
              updateRequest.onerror = () => reject(updateRequest.error);
            } else {
              resolve(); // Annotation not found in language storage
            }
          } else {
            resolve(); // Language entry or annotations don't exist
          }
        };
        languageRequest.onerror = () => reject(languageRequest.error);
      };
      fileRequest.onerror = () => reject(fileRequest.error);
    });
  }

  // Helper function to update annotation in current language's annotations
  async updateAnnotationInLanguage(fileId, elementId, annotation, languageStore) {
    const file = await this.getFile(fileId);
    if (!file) return;

    const currentLanguage = file.default_language || 'en';
    const languageIndex = languageStore.index('file_language');
    const languageRequest = languageIndex.get([fileId, currentLanguage]);

    return new Promise((resolve, reject) => {
      languageRequest.onsuccess = () => {
        const languageEntry = languageRequest.result;
        if (languageEntry && languageEntry.annotations && languageEntry.annotations[elementId]) {
          // Find and update the annotation
          const annotations = languageEntry.annotations[elementId];
          const index = annotations.findIndex(a => a.id === annotation.id);
          if (index !== -1) {
            annotations[index] = {
              id: annotation.id,
              type: annotation.type,
              content: annotation.content,
              audio_data: annotation.audio_data,
              duration: annotation.duration,
              created: annotation.created,
              user_name: annotation.user_name
            };

            languageEntry.last_modified = new Date();
            const updateRequest = languageStore.put(languageEntry);
            updateRequest.onsuccess = () => resolve();
            updateRequest.onerror = () => reject(updateRequest.error);
          } else {
            resolve(); // Annotation not found in language storage
          }
        } else {
          resolve(); // Language entry or annotations don't exist
        }
      };
      languageRequest.onerror = () => reject(languageRequest.error);
    });
  }

  // Get annotations for file and language
  async getAnnotations(fileId, languageCode = null) {
    if (!this.db) {
      console.warn('Database not initialized in getAnnotations - returning empty array');
      return [];
    }

    // Get the current language if not provided
    if (!languageCode) {
      const file = await this.getFile(fileId);
      languageCode = file?.default_language || 'en';
    }

    const tx = await this.transaction([STORES.ANNOTATIONS]);
    const store = tx.objectStore(STORES.ANNOTATIONS);
    const index = store.index('file_id');

    return new Promise((resolve, reject) => {
      const annotations = [];
      const request = index.openCursor(fileId);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const annotation = cursor.value;
          // Only include annotations for the current language
          if (annotation.language_code === languageCode) {
            annotations.push(annotation);
          }
          cursor.continue();
        } else {
          console.log(`Retrieved ${annotations.length} annotations for ${languageCode}`);
          resolve(annotations);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Get annotation by server ID
  async getAnnotationByServerId(serverId) {
    if (!this.db) {
      console.warn('Database not initialized in getAnnotationByServerId - returning null');
      return null;
    }

    const tx = await this.transaction([STORES.ANNOTATIONS]);
    const store = tx.objectStore(STORES.ANNOTATIONS);

    return new Promise((resolve, reject) => {
      const request = store.openCursor();

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const annotation = cursor.value;
          // Check if this annotation has the server ID we're looking for
          if (annotation.server_id === serverId) {
            resolve(annotation);
            return;
          }
          cursor.continue();
        } else {
          resolve(null); // Not found
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Remove duplicate annotations (same content, element_id, and type)
  async removeDuplicateAnnotations(fileId) {
    const annotations = await this.getAnnotations(fileId);
    const seen = new Map();
    const toDelete = [];

    // Group by element_id + type + content to find duplicates
    annotations.forEach(annotation => {
      const key = `${annotation.element_id}-${annotation.type}-${annotation.content}`;
      if (seen.has(key)) {
        // Keep the newer annotation (larger timestamp)
        const existing = seen.get(key);
        if (new Date(annotation.created) > new Date(existing.created)) {
          toDelete.push(existing.id);
          seen.set(key, annotation);
        } else {
          toDelete.push(annotation.id);
        }
      } else {
        seen.set(key, annotation);
      }
    });

    // Delete duplicates
    const tx = await this.transaction([STORES.ANNOTATIONS], 'readwrite');
    const store = tx.objectStore(STORES.ANNOTATIONS);

    for (const id of toDelete) {
      await new Promise((resolve, reject) => {
        const request = store.delete(id);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    }

    console.log(`Removed ${toDelete.length} duplicate annotations`);
    return toDelete.length;
  }

  // Delete annotation
  async deleteAnnotation(annotationId) {
    // First get the annotation details before deleting
    const tx1 = await this.transaction([STORES.ANNOTATIONS]);
    const store1 = tx1.objectStore(STORES.ANNOTATIONS);

    const annotation = await new Promise((resolve, reject) => {
      const request = store1.get(annotationId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (!annotation) {
      return false; // Annotation not found
    }

    // Delete from annotations table
    const tx2 = await this.transaction([STORES.ANNOTATIONS], 'readwrite');
    const store2 = tx2.objectStore(STORES.ANNOTATIONS);

    const deleted = await new Promise((resolve, reject) => {
      const request = store2.delete(annotationId);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });

    if (deleted) {
      // Also remove from language data
      try {
        await this.removeAnnotationFromCurrentLanguage(
          annotation.file_id,
          annotation.element_id,
          annotation.id
        );
        console.log(`Deleted annotation ${annotationId} from both table and language data`);
      } catch (error) {
        console.warn('Could not remove annotation from language data:', error);
      }
    }

    return deleted;
  }

  // PREFERENCE OPERATIONS

  // PREFERENCE OPERATIONS REMOVED
  // Preferences are now stored in localStorage and synced via server
  // See: useEditorPreferences.js and syncService.js

  // METADATA OPERATIONS

  // Save app metadata
  async saveMetadata(key, value) {
    const metadata = { key, value, last_modified: new Date() };
    const tx = await this.transaction([STORES.METADATA], 'readwrite');
    const store = tx.objectStore(STORES.METADATA);

    return new Promise((resolve, reject) => {
      const request = store.put(metadata);
      request.onsuccess = () => resolve(metadata);
      request.onerror = () => reject(request.error);
    });
  }

  // Get app metadata
  async getMetadata(key) {
    const tx = await this.transaction([STORES.METADATA]);
    const store = tx.objectStore(STORES.METADATA);

    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result?.value || null);
      request.onerror = () => reject(request.error);
    });
  }



  // CHAT HISTORY OPERATIONS (renamed from chat contexts to match server)

  // Save chat history message
  async saveChatHistory(chatData) {
    console.log('[SessionMetadata] IndexedDB saveChatHistory received chatData:', chatData);
    console.log('[SessionMetadata] IndexedDB session_metadata field:', chatData.session_metadata);

    // CRITICAL: Extract fileId from contextData if not provided at top level (already fixed at useChatMessages level)
    const fileId = chatData.fileId || chatData.contextData?.fileId || null;

    // ✅ FIXED: Support both sessionMetadata (camelCase - API format) and session_metadata (snake_case - old format)
    const sessionMetadata = chatData.sessionMetadata || chatData.session_metadata || null;

    const chatHistory = {
      id: chatData.id || this.generateId(),
      server_id: chatData.server_id || null, // server-side ID if synced from server
      userId: chatData.userId || this.currentUserId,
      sessionId: chatData.sessionId || '',
      fileId: fileId, // ✅ FIXED: fileId as separate top-level column
      messageType: chatData.messageType || 'user_input',
      content: chatData.content || '',
      mode: chatData.mode || 'chat',
      messageMode: chatData.messageMode || chatData.mode, // FIXED: Add messageMode for proper display (ask/plan/format)
      template: chatData.template || null,
      category: chatData.category || 'screenplay', // FIXED: Add category field for mode filtering
      formattedElements: chatData.formattedElements || null,
      artifactType: chatData.artifactType || null, // artifact display type (screenplay, lyrics, etc.)
      artifactTitle: chatData.artifactTitle || null, // artifact display title
      steps: chatData.steps || null, // orchestrator streaming steps for history display
      originalText: chatData.originalText || null,
      contextData: chatData.contextData || null,
      messageContext: chatData.messageContext || null,
      tokensUsed: chatData.tokensUsed || 0,
      tokens_input: chatData.tokens_input || 0,
      tokens_output: chatData.tokens_output || 0,
      cost_usd: chatData.cost_usd || 0,
      synced: chatData.synced !== undefined ? chatData.synced : false, // default to false for new messages
      model_used: chatData.model_used || null, // FIXED: Add model_used field
      sessionMetadata: sessionMetadata, // ✅ FIXED: Store as sessionMetadata (camelCase) to match backend API
      attachments: chatData.attachments || null, // Store attachment metadata (documents/audio with S3 URLs)
      createdAt: chatData.createdAt || new Date()
    };

    console.log('[SessionMetadata] IndexedDB prepared chatHistory object:', chatHistory);
    console.log('[SessionMetadata] IndexedDB fileId being stored:', chatHistory.fileId);
    console.log('[SessionMetadata] IndexedDB sessionMetadata being stored:', chatHistory.sessionMetadata);

    const tx = await this.transaction([STORES.CHAT_HISTORIES], 'readwrite');
    const store = tx.objectStore(STORES.CHAT_HISTORIES);

    return new Promise((resolve, reject) => {
      const request = store.put(chatHistory); // Use put to allow updates
      request.onsuccess = () => resolve(chatHistory);
      request.onerror = () => reject(request.error);
    });
  }

  // Get chat histories by session OR by userId (if sessionId not provided)
  async getChatHistories(sessionIdOrUserId) {
    const tx = await this.transaction([STORES.CHAT_HISTORIES]);
    const store = tx.objectStore(STORES.CHAT_HISTORIES);

    return new Promise((resolve, reject) => {
      const histories = [];

      // Try to get by sessionId first
      const sessionIndex = store.index('sessionId');
      const sessionRequest = sessionIndex.openCursor(sessionIdOrUserId);

      sessionRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          histories.push(cursor.value);
          cursor.continue();
        } else {
          // If we found histories, return them
          if (histories.length > 0) {
            histories.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
            resolve(histories);
          } else {
            // Otherwise, try by userId
            const userIndex = store.index('userId');
            const userRequest = userIndex.openCursor(sessionIdOrUserId);

            userRequest.onsuccess = (event) => {
              const cursor = event.target.result;
              if (cursor) {
                histories.push(cursor.value);
                cursor.continue();
              } else {
                histories.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                resolve(histories);
              }
            };

            userRequest.onerror = () => reject(userRequest.error);
          }
        }
      };

      sessionRequest.onerror = () => reject(sessionRequest.error);
    });
  }

  // Get all unsynced chat histories
  async getUnsyncedChatHistories() {
    const tx = await this.transaction([STORES.CHAT_HISTORIES]);
    const store = tx.objectStore(STORES.CHAT_HISTORIES);

    return new Promise((resolve, reject) => {
      const request = store.getAll();

      request.onsuccess = () => {
        const allHistories = request.result || [];
        // Filter for unsynced (synced === false or synced === undefined/null)
        const unsynced = allHistories.filter(chat => !chat.synced);
        resolve(unsynced);
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Mark chat histories as synced
  async markChatHistoriesAsSynced(chatIds) {
    const tx = await this.transaction([STORES.CHAT_HISTORIES], 'readwrite');
    const store = tx.objectStore(STORES.CHAT_HISTORIES);

    return new Promise((resolve, reject) => {
      let updated = 0;
      const promises = chatIds.map(chatId => {
        return new Promise((res, rej) => {
          const getRequest = store.get(chatId);
          getRequest.onsuccess = () => {
            const chat = getRequest.result;
            if (chat) {
              chat.synced = true;
              const putRequest = store.put(chat);
              putRequest.onsuccess = () => {
                updated++;
                res();
              };
              putRequest.onerror = () => rej(putRequest.error);
            } else {
              res(); // Chat not found, skip
            }
          };
          getRequest.onerror = () => rej(getRequest.error);
        });
      });

      Promise.all(promises)
        .then(() => resolve(updated))
        .catch(reject);
    });
  }

  // Clear old chat histories (older than 30 days)
  async clearOldChatHistories(daysOld = 30) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);

    const tx = await this.transaction([STORES.CHAT_HISTORIES], 'readwrite');
    const store = tx.objectStore(STORES.CHAT_HISTORIES);
    const index = store.index('createdAt');

    return new Promise((resolve, reject) => {
      let deletedCount = 0;
      const request = index.openCursor();

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const history = cursor.value;
          if (new Date(history.createdAt) < cutoffDate) {
            const deleteRequest = store.delete(history.id);
            deleteRequest.onsuccess = () => deletedCount++;
          }
          cursor.continue();
        } else {
          resolve(deletedCount);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Delete all chat histories for a specific session
  async deleteChatSession(sessionId) {
    const tx = await this.transaction([STORES.CHAT_HISTORIES], 'readwrite');
    const store = tx.objectStore(STORES.CHAT_HISTORIES);
    const index = store.index('sessionId');

    return new Promise((resolve, reject) => {
      let deletedCount = 0;
      const request = index.openCursor(sessionId);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const deleteRequest = store.delete(cursor.value.id);
          deleteRequest.onsuccess = () => {
            deletedCount++;
            cursor.continue();
          };
          deleteRequest.onerror = () => reject(deleteRequest.error);
        } else {
          resolve(deletedCount);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Re-point every locally-stored chat history row from an old session id
  // to a new one -- the local-storage half of the server's
  // /api/chat-history/reassociate. Used when a project is created *from*
  // an already-in-progress chat (random session id, no project yet) so
  // messages already saved locally aren't orphaned under an id nothing will
  // look up again. Marks each row unsynced so the next sync push carries
  // the new sessionId to the server too (the caller is expected to also
  // call the server route directly for rows already synced -- this handles
  // the local cache and any not-yet-synced rows uniformly).
  async reassociateChatSession(oldSessionId, newSessionId) {
    if (!oldSessionId || !newSessionId || oldSessionId === newSessionId) return 0;
    const tx = await this.transaction([STORES.CHAT_HISTORIES], 'readwrite');
    const store = tx.objectStore(STORES.CHAT_HISTORIES);
    const index = store.index('sessionId');

    return new Promise((resolve, reject) => {
      let updatedCount = 0;
      const request = index.openCursor(oldSessionId);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const updated = { ...cursor.value, sessionId: newSessionId, synced: false };
          const putRequest = cursor.update(updated);
          putRequest.onsuccess = () => {
            updatedCount++;
            cursor.continue();
          };
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve(updatedCount);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Legacy method names for backward compatibility
  async saveChatContext(sessionId, contextData) {
    return this.saveChatHistory({
      sessionId,
      messageType: contextData.type || 'user_input',
      content: contextData.content || '',
      contextData: contextData.metadata || {},
      createdAt: contextData.created || new Date()
    });
  }

  async getChatContexts(sessionId) {
    return this.getChatHistories(sessionId);
  }

  async clearOldChatContexts(daysOld = 30) {
    return this.clearOldChatHistories(daysOld);
  }

  // COLLABORATION OPERATIONS

  // Share file with user (add collaboration record)
  async shareFile(fileId, userEmail, permissionLevel = 'view', createdBy = null) {
    const collaboration = {
      id: this.generateId(),
      file_id: fileId,
      user_email: userEmail,
      permission_level: permissionLevel, // 'view' or 'edit'
      created_by: createdBy || this.currentUserId,
      created: new Date(),
      status: 'pending' // 'pending', 'accepted', 'declined'
    };

    const tx = await this.transaction([STORES.COLLABORATIONS], 'readwrite');
    const store = tx.objectStore(STORES.COLLABORATIONS);

    return new Promise((resolve, reject) => {
      const request = store.add(collaboration);
      request.onsuccess = () => resolve(collaboration);
      request.onerror = () => reject(request.error);
    });
  }

  // Get collaborations for a file
  async getFileCollaborations(fileId) {
    const tx = await this.transaction([STORES.COLLABORATIONS]);
    const store = tx.objectStore(STORES.COLLABORATIONS);
    const index = store.index('file_id');

    return new Promise((resolve, reject) => {
      const collaborations = [];
      const request = index.openCursor(fileId);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          collaborations.push(cursor.value);
          cursor.continue();
        } else {
          resolve(collaborations);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Remove collaboration (unshare file)
  async removeCollaboration(collaborationId) {
    const tx = await this.transaction([STORES.COLLABORATIONS], 'readwrite');
    const store = tx.objectStore(STORES.COLLABORATIONS);

    return new Promise((resolve, reject) => {
      const request = store.delete(collaborationId);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // Update collaboration permission
  async updateCollaborationPermission(collaborationId, newPermission) {
    const tx = await this.transaction([STORES.COLLABORATIONS], 'readwrite');
    const store = tx.objectStore(STORES.COLLABORATIONS);

    return new Promise((resolve, reject) => {
      const getRequest = store.get(collaborationId);
      getRequest.onsuccess = () => {
        const collaboration = getRequest.result;
        if (collaboration) {
          collaboration.permission_level = newPermission;
          collaboration.last_modified = new Date();
          const putRequest = store.put(collaboration);
          putRequest.onsuccess = () => resolve(collaboration);
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          reject(new Error('Collaboration not found'));
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // Check if user has access to file
  async checkFileAccess(fileId, userId = null) {
    const checkUserId = userId || this.currentUserId;

    // Check if user owns the file
    const file = await this.getFile(fileId);
    if (file && file.user_id === checkUserId) {
      return { hasAccess: true, permission: 'owner' };
    }

    // Check collaborations
    const tx = await this.transaction([STORES.COLLABORATIONS]);
    const store = tx.objectStore(STORES.COLLABORATIONS);
    const index = store.index('file_id');

    return new Promise((resolve, reject) => {
      const request = index.openCursor(fileId);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const collaboration = cursor.value;
          if (collaboration.user_email === checkUserId && collaboration.status === 'accepted') {
            resolve({ hasAccess: true, permission: collaboration.permission_level });
            return;
          }
          cursor.continue();
        } else {
          resolve({ hasAccess: false, permission: null });
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Get files shared with current user
  async getSharedWithMeFiles() {
    const tx = await this.transaction([STORES.COLLABORATIONS, STORES.FILES]);
    const collaborationStore = tx.objectStore(STORES.COLLABORATIONS);
    const filesStore = tx.objectStore(STORES.FILES);
    const index = collaborationStore.index('user_email');

    return new Promise((resolve, reject) => {
      const sharedFiles = [];
      const request = index.openCursor(this.currentUserId);

      request.onsuccess = async (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const collaboration = cursor.value;

          // Only include accepted collaborations
          if (collaboration.status === 'accepted') {
            try {
              // Get the file details
              const fileRequest = filesStore.get(collaboration.file_id);
              fileRequest.onsuccess = () => {
                const file = fileRequest.result;
                if (file) {
                  sharedFiles.push({
                    ...file,
                    collaboration: {
                      id: collaboration.id,
                      permission: collaboration.permission_level,
                      sharedAt: collaboration.created,
                      status: collaboration.status
                    }
                  });
                }
              };
            } catch (error) {
              console.warn('Error loading shared file:', error);
            }
          }
          cursor.continue();
        } else {
          // Sort by most recent collaboration
          sharedFiles.sort((a, b) =>
            new Date(b.collaboration.sharedAt) - new Date(a.collaboration.sharedAt)
          );
          resolve(sharedFiles);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // SHARED FILE OPERATIONS

  // Save a shared file locally with its content and metadata
  async saveSharedFile(fileData, collaborationData) {
    const tx = await this.transaction([STORES.FILES, STORES.FILE_LANGUAGES, STORES.COLLABORATIONS], 'readwrite');
    const filesStore = tx.objectStore(STORES.FILES);
    const languagesStore = tx.objectStore(STORES.FILE_LANGUAGES);
    const collaborationsStore = tx.objectStore(STORES.COLLABORATIONS);

    return new Promise((resolve, reject) => {
      // Save the main file record
      const fileRecord = {
        id: fileData.id,
        project_id: fileData.projectId || fileData.project_id,
        // Keyed by the CURRENT (collaborator) user, not the file's owner --
        // listFiles() queries the FILES store by a user_id index scoped to
        // whoever is logged in locally, so an owner-keyed row here is
        // invisible to that lookup and the shared file never appears.
        // The original owner is preserved separately below as `shared_by`.
        user_id: this.currentUserId,
        name: fileData.name,
        created: new Date(fileData.createdAt || fileData.created),
        last_modified: new Date(fileData.updatedAt || fileData.last_modified),
        template_id: fileData.templateId || fileData.template_id,
        title_page: fileData.titlePage || fileData.title_page || {},
        content: fileData.content || '',
        pinned_locations: fileData.pinnedLocations || fileData.pinned_locations || {},
        annotations: fileData.annotations || {},
        default_language: fileData.defaultLanguage || fileData.default_language || 'en',
        available_languages: fileData.availableLanguages || fileData.available_languages || ['en'],
        // Mark as shared file
        is_shared: true,
        shared_by: collaborationData.ownerId || collaborationData.owner_id
      };

      const fileRequest = filesStore.put(fileRecord);

      fileRequest.onsuccess = () => {
        // Save collaboration record
        const collaborationRecord = {
          id: collaborationData.id,
          file_id: fileData.id,
          owner_id: collaborationData.ownerId || collaborationData.owner_id,
          user_email: this.currentUserId, // Current user's email
          permission_level: collaborationData.permission,
          status: collaborationData.status || 'accepted',
          created: new Date(collaborationData.createdAt || collaborationData.created),
          server_synced: true,
          server_id: collaborationData.id
        };

        const collaborationRequest = collaborationsStore.put(collaborationRecord);

        collaborationRequest.onsuccess = () => {
          // If fileLanguages data is provided, save it
          if (fileData.fileLanguages && Array.isArray(fileData.fileLanguages) && fileData.fileLanguages.length > 0) {
            let languageSaveCount = 0;
            const totalLanguages = fileData.fileLanguages.length;

            fileData.fileLanguages.forEach(langData => {
              const languageRecord = {
                id: langData.id || `${fileData.id}_${langData.languageCode}`,
                file_id: fileData.id,
                language_code: langData.languageCode,
                language_name: langData.languageName || langData.language_name,
                content: langData.content || '',
                title_page: langData.titlePage || langData.title_page || {},
                annotations: langData.annotations || {},
                pinned_locations: langData.pinnedLocations || langData.pinned_locations || {},
                last_modified: new Date(langData.updatedAt || langData.last_modified || fileData.updatedAt),
                version: langData.version || 1,
                server_synced: true
              };

              const langRequest = languagesStore.put(languageRecord);
              langRequest.onsuccess = () => {
                languageSaveCount++;
                if (languageSaveCount === totalLanguages) {
                  resolve({ file: fileRecord, collaboration: collaborationRecord });
                }
              };
              langRequest.onerror = () => reject(langRequest.error);
            });
          } else {
            resolve({ file: fileRecord, collaboration: collaborationRecord });
          }
        };

        collaborationRequest.onerror = () => reject(collaborationRequest.error);
      };

      fileRequest.onerror = () => reject(fileRequest.error);
    });
  }

  // Load shared file with full content (for offline access)
  async loadSharedFileContent(fileId) {
    const tx = await this.transaction([STORES.FILES, STORES.FILE_LANGUAGES, STORES.COLLABORATIONS]);
    const filesStore = tx.objectStore(STORES.FILES);
    const languagesStore = tx.objectStore(STORES.FILE_LANGUAGES);
    const collaborationsStore = tx.objectStore(STORES.COLLABORATIONS);

    return new Promise((resolve, reject) => {
      const fileRequest = filesStore.get(fileId);

      fileRequest.onsuccess = () => {
        const file = fileRequest.result;
        if (!file) {
          resolve(null);
          return;
        }

        // Get file languages
        const languageIndex = languagesStore.index('file_id');
        const languageRequest = languageIndex.getAll(fileId);

        languageRequest.onsuccess = () => {
          const languages = languageRequest.result || [];

          // Get collaboration info
          const collaborationIndex = collaborationsStore.index('file_id');
          const collaborationRequest = collaborationIndex.get(fileId);

          collaborationRequest.onsuccess = () => {
            const collaboration = collaborationRequest.result;

            resolve({
              ...file,
              fileLanguages: languages,
              collaboration: collaboration
            });
          };

          collaborationRequest.onerror = () => reject(collaborationRequest.error);
        };

        languageRequest.onerror = () => reject(languageRequest.error);
      };

      fileRequest.onerror = () => reject(fileRequest.error);
    });
  }

  // Remove shared file (when user leaves collaboration)
  async removeSharedFile(fileId) {
    const tx = await this.transaction([STORES.FILES, STORES.FILE_LANGUAGES, STORES.COLLABORATIONS], 'readwrite');
    const filesStore = tx.objectStore(STORES.FILES);
    const languagesStore = tx.objectStore(STORES.FILE_LANGUAGES);
    const collaborationsStore = tx.objectStore(STORES.COLLABORATIONS);

    return new Promise((resolve, reject) => {
      // Remove file languages
      const languageIndex = languagesStore.index('file_id');
      const languageRequest = languageIndex.openCursor(IDBKeyRange.only(fileId));

      languageRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          // Remove collaboration record
          const collaborationIndex = collaborationsStore.index('file_id');
          const collaborationRequest = collaborationIndex.openCursor(IDBKeyRange.only(fileId));

          collaborationRequest.onsuccess = (event) => {
            const cursor = event.target.result;
            if (cursor) {
              cursor.delete();
              cursor.continue();
            } else {
              // Remove main file record
              const fileRequest = filesStore.delete(fileId);
              fileRequest.onsuccess = () => resolve(true);
              fileRequest.onerror = () => reject(fileRequest.error);
            }
          };

          collaborationRequest.onerror = () => reject(collaborationRequest.error);
        }
      };

      languageRequest.onerror = () => reject(languageRequest.error);
    });
  }

  // MULTILINGUAL FILE OPERATIONS

  // Get file content in specific language
  async getFileInLanguage(fileId, languageCode) {
    const tx = await this.transaction([STORES.FILE_LANGUAGES]);
    const store = tx.objectStore(STORES.FILE_LANGUAGES);
    const index = store.index('file_language');

    return new Promise((resolve, reject) => {
      const request = index.get([fileId, languageCode]);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Save file content in specific language
  async saveFileInLanguage(fileId, languageCode, content, titlePage = null, additionalData = {}) {
    const tx = await this.transaction([STORES.FILE_LANGUAGES, STORES.FILES], 'readwrite');
    const languagesStore = tx.objectStore(STORES.FILE_LANGUAGES);
    const filesStore = tx.objectStore(STORES.FILES);
    const index = languagesStore.index('file_language');

    return new Promise((resolve, reject) => {
      // Check for existing entry within the same transaction to avoid race conditions
      const checkRequest = index.get([fileId, languageCode]);
      checkRequest.onsuccess = () => {
        const existing = checkRequest.result;

        let entryData;
        if (existing) {
          // Update existing language entry
          entryData = {
            ...existing,
            content,
            title_page: titlePage || existing.title_page,
            annotations: additionalData.annotations !== undefined ? additionalData.annotations : existing.annotations,
            pinned_locations: additionalData.pinned_locations !== undefined ? additionalData.pinned_locations : existing.pinned_locations,
            last_modified: additionalData.last_modified || new Date(),
            ...additionalData // Allow override of any other fields
          };
        } else {
          // Create new language entry
          entryData = {
            id: this.generateId(),
            file_id: fileId,
            language_code: languageCode,
            language_name: this.getLanguageName(languageCode),
            content,
            title_page: titlePage || {},
            annotations: additionalData.annotations || {},
            pinned_locations: additionalData.pinned_locations || {},
            created: additionalData.created || new Date(),
            last_modified: additionalData.last_modified || new Date(),
            ...additionalData // Allow override of any other fields
          };
        }

        // Always use put() to handle both insert and update cases safely
        const request = languagesStore.put(entryData);

        request.onsuccess = () => {
          // Update main file to include new language (only for new entries)
          if (!existing) {
            const fileRequest = filesStore.get(fileId);
            fileRequest.onsuccess = () => {
              const file = fileRequest.result;
              if (file && file.available_languages) {
                if (!file.available_languages.includes(languageCode)) {
                  file.available_languages.push(languageCode);
                  file.last_modified = new Date();
                  const updateRequest = filesStore.put(file);
                  updateRequest.onsuccess = () => resolve(entryData);
                  updateRequest.onerror = () => reject(updateRequest.error);
                } else {
                  resolve(entryData);
                }
              } else {
                resolve(entryData);
              }
            };
            fileRequest.onerror = () => resolve(entryData);
          } else {
            // For existing entries, just update the main file's last_modified
            const fileRequest = filesStore.get(fileId);
            fileRequest.onsuccess = () => {
              const file = fileRequest.result;
              if (file) {
                file.last_modified = new Date();
                const updateRequest = filesStore.put(file);
                updateRequest.onsuccess = () => resolve(entryData);
                updateRequest.onerror = () => reject(updateRequest.error);
              } else {
                resolve(entryData);
              }
            };
            fileRequest.onerror = () => resolve(entryData);
          }
        };

        request.onerror = () => reject(request.error);
      };
      checkRequest.onerror = () => reject(checkRequest.error);
    });
  }

  // Update file content in specific language (alias for saveFileInLanguage which handles both create and update)
  async updateFileInLanguage(fileId, languageCode, content, titlePage = null, additionalData = {}) {
    return await this.saveFileInLanguage(fileId, languageCode, content, titlePage, additionalData);
  }

  // Get all languages for a file
  async getFileLanguages(fileId) {
    const tx = await this.transaction([STORES.FILE_LANGUAGES]);
    const store = tx.objectStore(STORES.FILE_LANGUAGES);
    const index = store.index('file_id');

    return new Promise((resolve, reject) => {
      const languages = [];
      const request = index.openCursor(fileId);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          languages.push(cursor.value);
          cursor.continue();
        } else {
          resolve(languages);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Add new language to file
  async addLanguageToFile(fileId, languageCode, copyFrom = null) {
    const file = await this.getFile(fileId);
    if (!file) throw new Error('File not found');

    // Check if language already exists
    const existing = await this.getFileInLanguage(fileId, languageCode);
    if (existing) {
      return existing;
    }

    let content = '';
    let titlePage = {};

    if (copyFrom) {
      // Copy content from another language
      const sourceLanguage = await this.getFileInLanguage(fileId, copyFrom);
      if (sourceLanguage) {
        content = sourceLanguage.content;
        titlePage = sourceLanguage.title_page;
      }
    } else {
      // Copy from default language or main file
      const defaultLanguage = await this.getFileInLanguage(fileId, file.default_language);
      if (defaultLanguage) {
        content = defaultLanguage.content;
        titlePage = defaultLanguage.title_page;
      } else {
        content = file.content;
        titlePage = file.title_page;
      }
    }

    return await this.saveFileInLanguage(fileId, languageCode, content, titlePage);
  }

  // Remove language from file
  async removeLanguageFromFile(fileId, languageCode) {
    const file = await this.getFile(fileId);
    if (!file) throw new Error('File not found');

    // Cannot remove default language
    if (languageCode === file.default_language) {
      throw new Error('Cannot remove default language');
    }

    const tx = await this.transaction([STORES.FILE_LANGUAGES, STORES.FILES], 'readwrite');
    const languagesStore = tx.objectStore(STORES.FILE_LANGUAGES);
    const filesStore = tx.objectStore(STORES.FILES);
    const index = languagesStore.index('file_language');

    return new Promise((resolve, reject) => {
      const request = index.get([fileId, languageCode]);
      request.onsuccess = () => {
        const languageEntry = request.result;
        if (languageEntry) {
          const deleteRequest = languagesStore.delete(languageEntry.id);
          deleteRequest.onsuccess = () => {
            // Update file's available languages
            const fileRequest = filesStore.get(fileId);
            fileRequest.onsuccess = () => {
              const file = fileRequest.result;
              if (file && file.available_languages) {
                file.available_languages = file.available_languages.filter(lang => lang !== languageCode);
                file.last_modified = new Date();
                const updateRequest = filesStore.put(file);
                updateRequest.onsuccess = () => resolve(true);
                updateRequest.onerror = () => reject(updateRequest.error);
              } else {
                resolve(true);
              }
            };
            fileRequest.onerror = () => resolve(true);
          };
          deleteRequest.onerror = () => reject(deleteRequest.error);
        } else {
          resolve(false);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // PINNED LOCATIONS OPERATIONS

  // Save pinned location (similar to annotation pattern)
  async savePinnedLocation(fileId, locationData, languageCode = null) {
    // Get the current language if not provided
    if (!languageCode) {
      const file = await this.getFile(fileId);
      languageCode = file?.default_language || 'en';
    }

    // Update the file language data with pinned locations
    const existingLanguageData = await this.getFileInLanguage(fileId, languageCode);
    if (!existingLanguageData) {
      console.warn(`Language data not found for ${languageCode}, cannot save pinned location`);
      return null;
    }

    // Replace pinned locations completely (don't merge to allow deletions)
    const updatedPinnedLocations = locationData;

    // Save back to file language data, preserving content
    return await this.saveFileInLanguage(
      fileId,
      languageCode,
      existingLanguageData.content, // Preserve existing content
      existingLanguageData.title_page, // Preserve existing title page
      {
        pinned_locations: updatedPinnedLocations,
        annotations: existingLanguageData.annotations, // Preserve existing annotations
        last_modified: new Date()
      }
    );
  }

  // Get pinned locations for file and language
  async getPinnedLocations(fileId, languageCode = null) {
    if (!languageCode) {
      const file = await this.getFile(fileId);
      languageCode = file?.default_language || 'en';
    }

    const languageData = await this.getFileInLanguage(fileId, languageCode);
    return languageData?.pinned_locations || {};
  }

  // UTILITY METHODS

  // Clear all data (for testing)
  async clearAllData() {
    const tx = await this.transaction(Object.values(STORES), 'readwrite');

    const promises = Object.values(STORES).map(storeName => {
      const store = tx.objectStore(storeName);
      return new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });

    return Promise.all(promises);
  }

  // Get database info
  async getDbInfo() {
    const fileCount = await this.getStoreCount(STORES.FILES);
    const annotationCount = await this.getStoreCount(STORES.ANNOTATIONS);

    return {
      files: fileCount,
      annotations: annotationCount,
      userId: this.currentUserId,
      dbVersion: DB_VERSION
    };
  }

  // Get store count
  async getStoreCount(storeName) {
    const tx = await this.transaction([storeName]);
    const store = tx.objectStore(storeName);

    return new Promise((resolve, reject) => {
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // PUBLIC SHARING OPERATIONS

  // Create public share
  async createPublicShare(fileId, shareData) {
    const publicShare = {
      id: this.generateId(),
      file_id: fileId,
      owner_user_id: this.currentUserId,
      share_token: shareData.share_token || this.generatePublicUrl(),
      requires_nda: shareData.requires_nda || false,
      nda_text: shareData.nda_text || '',
      watermark_enabled: shareData.watermark_enabled || false,
      watermark_text: shareData.watermark_text || '',
      allow_pdf_download: shareData.allow_pdf_download || false,
      is_active: true,
      created: new Date(),
      last_modified: new Date(),
      access_count: 0
    };

    const tx = await this.transaction([STORES.PUBLIC_SHARES], 'readwrite');
    const store = tx.objectStore(STORES.PUBLIC_SHARES);

    return new Promise((resolve, reject) => {
      const request = store.add(publicShare);
      request.onsuccess = () => resolve(publicShare);
      request.onerror = () => reject(request.error);
    });
  }

  // Get public share by token
  async getPublicShareByToken(shareToken) {
    const tx = await this.transaction([STORES.PUBLIC_SHARES]);
    const store = tx.objectStore(STORES.PUBLIC_SHARES);
    const index = store.index('share_token');

    return new Promise((resolve, reject) => {
      const request = index.get(shareToken);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Get public shares for file
  async getFilePublicShares(fileId) {
    const tx = await this.transaction([STORES.PUBLIC_SHARES]);
    const store = tx.objectStore(STORES.PUBLIC_SHARES);
    const index = store.index('file_id');

    return new Promise((resolve, reject) => {
      const shares = [];
      const request = index.openCursor(fileId);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          shares.push(cursor.value);
          cursor.continue();
        } else {
          resolve(shares);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Update public share
  async updatePublicShare(shareId, updates) {
    const tx = await this.transaction([STORES.PUBLIC_SHARES], 'readwrite');
    const store = tx.objectStore(STORES.PUBLIC_SHARES);

    return new Promise((resolve, reject) => {
      const getRequest = store.get(shareId);
      getRequest.onsuccess = () => {
        const share = getRequest.result;
        if (share) {
          const updatedShare = {
            ...share,
            ...updates,
            last_modified: new Date()
          };
          const putRequest = store.put(updatedShare);
          putRequest.onsuccess = () => resolve(updatedShare);
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          reject(new Error('Public share not found'));
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // Increment access count
  async incrementShareAccess(shareId) {
    const tx = await this.transaction([STORES.PUBLIC_SHARES], 'readwrite');
    const store = tx.objectStore(STORES.PUBLIC_SHARES);

    return new Promise((resolve, reject) => {
      const getRequest = store.get(shareId);
      getRequest.onsuccess = () => {
        const share = getRequest.result;
        if (share) {
          share.access_count = (share.access_count || 0) + 1;
          share.last_accessed = new Date();
          const putRequest = store.put(share);
          putRequest.onsuccess = () => resolve(share);
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          reject(new Error('Public share not found'));
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // Delete public share
  async deletePublicShare(shareId) {
    const tx = await this.transaction([STORES.PUBLIC_SHARES], 'readwrite');
    const store = tx.objectStore(STORES.PUBLIC_SHARES);

    return new Promise((resolve, reject) => {
      const request = store.delete(shareId);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // PUBLIC USER OPERATIONS

  // Record public user access
  async recordPublicUserAccess(publicShareId, userData) {
    const publicUser = {
      id: this.generateId(),
      public_share_id: publicShareId,
      email: userData.email,
      name: userData.name || '',
      signed_nda: userData.signed_nda || false,
      nda_signed_date: userData.signed_nda ? new Date() : null,
      ip_address: userData.ip_address || '',
      user_agent: userData.user_agent || '',
      access_date: new Date(),
      spotify_connected: false,
      spotify_session_data: null
    };

    const tx = await this.transaction([STORES.PUBLIC_USERS], 'readwrite');
    const store = tx.objectStore(STORES.PUBLIC_USERS);

    return new Promise((resolve, reject) => {
      const request = store.add(publicUser);
      request.onsuccess = () => resolve(publicUser);
      request.onerror = () => reject(request.error);
    });
  }

  // Get public users for share
  async getPublicUsersForShare(publicShareId) {
    const tx = await this.transaction([STORES.PUBLIC_USERS]);
    const store = tx.objectStore(STORES.PUBLIC_USERS);
    const index = store.index('public_share_id');

    return new Promise((resolve, reject) => {
      const users = [];
      const request = index.openCursor(publicShareId);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          users.push(cursor.value);
          cursor.continue();
        } else {
          resolve(users);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Update public user Spotify connection
  async updatePublicUserSpotify(userId, spotifyData) {
    const tx = await this.transaction([STORES.PUBLIC_USERS], 'readwrite');
    const store = tx.objectStore(STORES.PUBLIC_USERS);

    return new Promise((resolve, reject) => {
      const getRequest = store.get(userId);
      getRequest.onsuccess = () => {
        const user = getRequest.result;
        if (user) {
          user.spotify_connected = true;
          user.spotify_session_data = spotifyData;
          user.last_modified = new Date();
          const putRequest = store.put(user);
          putRequest.onsuccess = () => resolve(user);
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          reject(new Error('Public user not found'));
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // Generate public URL using UUID format for consistency
  generatePublicUrl() {
    return this.generateId(); // Use the same UUID generator for consistency
  }

  // AI IMPORT OPERATIONS

  // Create import job
  // Import job methods removed (import simplified to single API call)

  async createProject(projectData) {
    if (!this.db) await this.init();

    const project = {
      id: projectData.id || this.generateId(),
      userId: this.currentUserId,
      name: projectData.name || 'Untitled Project',
      description: projectData.description || '',
      createdAt: projectData.createdAt || new Date(),
      updatedAt: projectData.updatedAt || new Date()
    };

    const tx = await this.transaction([STORES.PROJECTS], 'readwrite');
    const store = tx.objectStore(STORES.PROJECTS);

    return new Promise((resolve, reject) => {
      const request = store.add(project);
      request.onsuccess = () => resolve(project);
      request.onerror = () => reject(request.error);
    });
  }

  // Get project by ID
  async getProject(projectId) {
    if (!this.db) await this.init();

    const tx = await this.transaction([STORES.PROJECTS]);
    const store = tx.objectStore(STORES.PROJECTS);

    return new Promise((resolve, reject) => {
      const request = store.get(projectId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Update project
  async updateProject(projectId, updates) {
    if (!this.db) await this.init();

    const project = await this.getProject(projectId);
    if (!project) {
      throw new Error('Project not found');
    }

    const updatedProject = {
      ...project,
      ...updates,
      updatedAt: new Date()
    };

    const tx = await this.transaction([STORES.PROJECTS], 'readwrite');
    const store = tx.objectStore(STORES.PROJECTS);

    return new Promise((resolve, reject) => {
      const request = store.put(updatedProject);
      request.onsuccess = () => resolve(updatedProject);
      request.onerror = () => reject(request.error);
    });
  }

  // Delete project
  async deleteProject(projectId) {
    if (!this.db) await this.init();

    const tx = await this.transaction([STORES.PROJECTS], 'readwrite');
    const store = tx.objectStore(STORES.PROJECTS);

    return new Promise((resolve, reject) => {
      const request = store.delete(projectId);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // List projects for current user
  async listProjects() {
    if (!this.db) await this.init();
    this._syncUserId();

    const tx = await this.transaction([STORES.PROJECTS]);
    const store = tx.objectStore(STORES.PROJECTS);
    const index = store.index('userId');

    return new Promise((resolve, reject) => {
      const projects = [];
      const request = index.openCursor(this.currentUserId);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          projects.push(cursor.value);
          cursor.continue();
        } else {
          // Sort by most recent
          projects.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
          resolve(projects);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Alias for listProjects (for convenience)
  async getAllProjects() {
    return this.listProjects();
  }

  // DOCUMENT NOTES OPERATIONS

  async createDocumentNote(noteData) {
    const note = {
      id: noteData.id || this.generateId(),
      projectId: noteData.projectId || null,
      fileId: noteData.fileId,
      userId: this.currentUserId,
      templateId: noteData.templateId,
      noteType: noteData.noteType,
      title: noteData.title || '',
      content: noteData.content || '',
      metadata: noteData.metadata || {},
      extractedFrom: noteData.extractedFrom || [],
      createdAt: noteData.createdAt || new Date(),
      updatedAt: noteData.updatedAt || new Date()
    };

    const tx = await this.transaction([STORES.DOCUMENT_NOTES], 'readwrite');
    const store = tx.objectStore(STORES.DOCUMENT_NOTES);

    return new Promise((resolve, reject) => {
      const request = store.add(note);
      request.onsuccess = () => resolve(note);
      request.onerror = () => reject(request.error);
    });
  }

  // Get document note by ID
  async getDocumentNote(noteId) {
    const tx = await this.transaction([STORES.DOCUMENT_NOTES]);
    const store = tx.objectStore(STORES.DOCUMENT_NOTES);

    return new Promise((resolve, reject) => {
      const request = store.get(noteId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Update document note
  async updateDocumentNote(noteId, updates) {
    const note = await this.getDocumentNote(noteId);
    if (!note) {
      throw new Error('Document note not found');
    }

    const updatedNote = {
      ...note,
      ...updates,
      updatedAt: new Date()
    };

    const tx = await this.transaction([STORES.DOCUMENT_NOTES], 'readwrite');
    const store = tx.objectStore(STORES.DOCUMENT_NOTES);

    return new Promise((resolve, reject) => {
      const request = store.put(updatedNote);
      request.onsuccess = () => resolve(updatedNote);
      request.onerror = () => reject(request.error);
    });
  }

  // Delete document note
  async deleteDocumentNote(noteId) {
    const tx = await this.transaction([STORES.DOCUMENT_NOTES], 'readwrite');
    const store = tx.objectStore(STORES.DOCUMENT_NOTES);

    return new Promise((resolve, reject) => {
      const request = store.delete(noteId);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // List document notes for file and template, or by projectId for standalone notes
  async listDocumentNotes(fileId, templateId = null, projectId = null) {
    const tx = await this.transaction([STORES.DOCUMENT_NOTES]);
    const store = tx.objectStore(STORES.DOCUMENT_NOTES);

    // For standalone notes (projectId provided, no fileId), get all notes for the project
    if (projectId && !fileId) {
      return new Promise((resolve, reject) => {
        const notes = [];
        const request = store.openCursor();

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            const note = cursor.value;
            // Match notes by projectId and null fileId
            if (note.projectId === projectId && note.fileId === null) {
              // Optional template filter
              if (!templateId || note.templateId === templateId) {
                notes.push(note);
              }
            }
            cursor.continue();
          } else {
            // Sort by order field (if exists), otherwise by updatedAt
            notes.sort((a, b) => {
              if (a.order !== undefined && b.order !== undefined) {
                return a.order - b.order;
              }
              if (a.order !== undefined) return -1;
              if (b.order !== undefined) return 1;
              return new Date(b.updatedAt) - new Date(a.updatedAt);
            });
            resolve(notes);
          }
        };

        request.onerror = () => reject(request.error);
      });
    }

    // For file-based notes, use the existing index-based query
    const index = templateId ? store.index('file_template') : store.index('fileId');
    const keyValue = templateId ? [fileId, templateId] : fileId;

    return new Promise((resolve, reject) => {
      const notes = [];
      const request = index.openCursor(keyValue);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          notes.push(cursor.value);
          cursor.continue();
        } else {
          // Sort by order field (if exists), otherwise by updatedAt
          notes.sort((a, b) => {
            // If both have order field, sort by order
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            }
            // If only one has order, put it first
            if (a.order !== undefined) return -1;
            if (b.order !== undefined) return 1;
            // Otherwise sort by most recent
            return new Date(b.updatedAt) - new Date(a.updatedAt);
          });
          resolve(notes);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // STANDALONE NOTES OPERATIONS (Decoupled from documents/templates)

  // Create standalone note
  async createStandaloneNote(noteData) {
    const note = {
      id: noteData.id || this.generateId(),
      title: noteData.title || '',
      content: noteData.content || '',
      color: noteData.color || null,
      createdAt: noteData.createdAt || new Date(),
      updatedAt: noteData.updatedAt || new Date(),
      metadata: {
        pinned: noteData.metadata?.pinned || false,
        noteType: noteData.metadata?.noteType || 'text',
        folderId: noteData.metadata?.folderId || null,
        ...(noteData.metadata || {})
      },
      // Initialize sync tracking
      syncStatus: noteData.syncStatus || 'pending',
      lastSyncedAt: noteData.lastSyncedAt || null,
      serverUpdatedAt: noteData.serverUpdatedAt || null
    };

    const tx = await this.transaction([STORES.STANDALONE_NOTES], 'readwrite');
    const store = tx.objectStore(STORES.STANDALONE_NOTES);

    return new Promise((resolve, reject) => {
      const request = store.add(note);
      request.onsuccess = () => resolve(note);
      request.onerror = () => reject(request.error);
    });
  }

  // Get standalone note by ID
  async getStandaloneNote(noteId) {
    const tx = await this.transaction([STORES.STANDALONE_NOTES]);
    const store = tx.objectStore(STORES.STANDALONE_NOTES);

    return new Promise((resolve, reject) => {
      const request = store.get(noteId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Update standalone note
  async updateStandaloneNote(noteId, updates) {
    const note = await this.getStandaloneNote(noteId);
    if (!note) {
      throw new Error('Standalone note not found');
    }

    const updatedNote = {
      ...note,
      ...updates,
      updatedAt: new Date(),
      // Merge metadata properly
      metadata: {
        ...note.metadata,
        ...(updates.metadata || {})
      }
    };

    const tx = await this.transaction([STORES.STANDALONE_NOTES], 'readwrite');
    const store = tx.objectStore(STORES.STANDALONE_NOTES);

    return new Promise((resolve, reject) => {
      const request = store.put(updatedNote);
      request.onsuccess = () => resolve(updatedNote);
      request.onerror = () => reject(request.error);
    });
  }

  // Delete standalone note
  async deleteStandaloneNote(noteId) {
    const tx = await this.transaction([STORES.STANDALONE_NOTES], 'readwrite');
    const store = tx.objectStore(STORES.STANDALONE_NOTES);

    return new Promise((resolve, reject) => {
      const request = store.delete(noteId);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // List all standalone notes
  async listStandaloneNotes() {
    const tx = await this.transaction([STORES.STANDALONE_NOTES]);
    const store = tx.objectStore(STORES.STANDALONE_NOTES);

    return new Promise((resolve, reject) => {
      const notes = [];
      const request = store.openCursor();

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          notes.push(cursor.value);
          cursor.continue();
        } else {
          // Sort by pinned first, then by updatedAt descending
          notes.sort((a, b) => {
            const aPinned = a.metadata?.pinned || false;
            const bPinned = b.metadata?.pinned || false;
            if (aPinned && !bPinned) return -1;
            if (!aPinned && bPinned) return 1;
            return new Date(b.updatedAt) - new Date(a.updatedAt);
          });
          resolve(notes);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // STANDALONE FOLDERS OPERATIONS

  // Create folder
  async createFolder(folderData) {
    const folder = {
      id: folderData.id || this.generateId(),
      name: folderData.name || 'New Folder',
      createdAt: folderData.createdAt || new Date()
    };

    const tx = await this.transaction([STORES.STANDALONE_FOLDERS], 'readwrite');
    const store = tx.objectStore(STORES.STANDALONE_FOLDERS);

    return new Promise((resolve, reject) => {
      const request = store.add(folder);
      request.onsuccess = () => resolve(folder);
      request.onerror = () => reject(request.error);
    });
  }

  // Get folder by ID
  async getFolder(folderId) {
    const tx = await this.transaction([STORES.STANDALONE_FOLDERS]);
    const store = tx.objectStore(STORES.STANDALONE_FOLDERS);

    return new Promise((resolve, reject) => {
      const request = store.get(folderId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Update folder
  async updateFolder(folderId, updates) {
    const folder = await this.getFolder(folderId);
    if (!folder) {
      throw new Error('Folder not found');
    }

    const updatedFolder = {
      ...folder,
      ...updates
    };

    const tx = await this.transaction([STORES.STANDALONE_FOLDERS], 'readwrite');
    const store = tx.objectStore(STORES.STANDALONE_FOLDERS);

    return new Promise((resolve, reject) => {
      const request = store.put(updatedFolder);
      request.onsuccess = () => resolve(updatedFolder);
      request.onerror = () => reject(request.error);
    });
  }

  // Delete folder
  async deleteFolder(folderId) {
    const tx = await this.transaction([STORES.STANDALONE_FOLDERS], 'readwrite');
    const store = tx.objectStore(STORES.STANDALONE_FOLDERS);

    return new Promise((resolve, reject) => {
      const request = store.delete(folderId);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // List all folders
  async listFolders() {
    const tx = await this.transaction([STORES.STANDALONE_FOLDERS]);
    const store = tx.objectStore(STORES.STANDALONE_FOLDERS);

    return new Promise((resolve, reject) => {
      const folders = [];
      const request = store.openCursor();

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          folders.push(cursor.value);
          cursor.continue();
        } else {
          // Sort by createdAt
          folders.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          resolve(folders);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // DOCUMENT OUTLINES OPERATIONS

  // Create document outline
  async createDocumentOutline(outlineData) {
    const outline = {
      id: outlineData.id || this.generateId(),
      fileId: outlineData.fileId,
      userId: this.currentUserId,
      templateId: outlineData.templateId,
      outlineType: outlineData.outlineType,
      title: outlineData.title || '',
      content: outlineData.content || '',
      aiGenerated: outlineData.aiGenerated || false,
      aiPromptUsed: outlineData.aiPromptUsed || null,
      metadata: outlineData.metadata || {},
      createdAt: outlineData.createdAt || new Date(),
      updatedAt: outlineData.updatedAt || new Date()
    };

    const tx = await this.transaction([STORES.DOCUMENT_OUTLINES], 'readwrite');
    const store = tx.objectStore(STORES.DOCUMENT_OUTLINES);

    return new Promise((resolve, reject) => {
      const request = store.add(outline);
      request.onsuccess = () => resolve(outline);
      request.onerror = () => reject(request.error);
    });
  }

  // Get document outline by ID
  async getDocumentOutline(outlineId) {
    const tx = await this.transaction([STORES.DOCUMENT_OUTLINES]);
    const store = tx.objectStore(STORES.DOCUMENT_OUTLINES);

    return new Promise((resolve, reject) => {
      const request = store.get(outlineId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Update document outline
  async updateDocumentOutline(outlineId, updates) {
    const outline = await this.getDocumentOutline(outlineId);
    if (!outline) {
      throw new Error('Document outline not found');
    }

    const updatedOutline = {
      ...outline,
      ...updates,
      updatedAt: new Date()
    };

    const tx = await this.transaction([STORES.DOCUMENT_OUTLINES], 'readwrite');
    const store = tx.objectStore(STORES.DOCUMENT_OUTLINES);

    return new Promise((resolve, reject) => {
      const request = store.put(updatedOutline);
      request.onsuccess = () => resolve(updatedOutline);
      request.onerror = () => reject(request.error);
    });
  }

  // Delete document outline
  async deleteDocumentOutline(outlineId) {
    const tx = await this.transaction([STORES.DOCUMENT_OUTLINES], 'readwrite');
    const store = tx.objectStore(STORES.DOCUMENT_OUTLINES);

    return new Promise((resolve, reject) => {
      const request = store.delete(outlineId);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // List document outlines for file and template
  async listDocumentOutlines(fileId, templateId = null) {
    const tx = await this.transaction([STORES.DOCUMENT_OUTLINES]);
    const store = tx.objectStore(STORES.DOCUMENT_OUTLINES);
    const index = templateId ? store.index('file_template') : store.index('fileId');
    const keyValue = templateId ? [fileId, templateId] : fileId;

    return new Promise((resolve, reject) => {
      const outlines = [];
      const request = index.openCursor(keyValue);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          outlines.push(cursor.value);
          cursor.continue();
        } else {
          // Sort by order field (if exists), otherwise by updatedAt
          outlines.sort((a, b) => {
            // If both have order field, sort by order
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            }
            // If only one has order, put it first
            if (a.order !== undefined) return -1;
            if (b.order !== undefined) return 1;
            // Otherwise sort by most recent
            return new Date(b.updatedAt) - new Date(a.updatedAt);
          });
          resolve(outlines);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // FILE METADATA OPERATIONS

  // Create file metadata
  async createFileMetadata(metadataData) {
    const metadata = {
      id: metadataData.id || this.generateId(),
      fileId: metadataData.fileId,
      userId: this.currentUserId,
      metadataType: metadataData.metadataType, // 'story', 'init_context', 'critique'
      templateId: metadataData.templateId || `file_${metadataData.metadataType}`, // e.g., 'file_story', 'file_init_context'
      title: metadataData.title || '',
      content: metadataData.content || '',
      formattedElements: metadataData.formattedElements || null, // Store formatted elements for rendering
      metadata: metadataData.metadata || {},
      createdAt: metadataData.createdAt || new Date(),
      updatedAt: metadataData.updatedAt || new Date()
    };

    const tx = await this.transaction([STORES.FILE_METADATA], 'readwrite');
    const store = tx.objectStore(STORES.FILE_METADATA);

    return new Promise((resolve, reject) => {
      const request = store.add(metadata);
      request.onsuccess = () => resolve(metadata);
      request.onerror = () => reject(request.error);
    });
  }

  // Get file metadata by fileId and type
  async getFileMetadata(fileId, metadataType) {
    const tx = await this.transaction([STORES.FILE_METADATA]);
    const store = tx.objectStore(STORES.FILE_METADATA);
    const index = store.index('file_metadataType');

    return new Promise((resolve, reject) => {
      const request = index.get([fileId, metadataType]);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Get file metadata by ID
  async getFileMetadataById(metadataId) {
    const tx = await this.transaction([STORES.FILE_METADATA]);
    const store = tx.objectStore(STORES.FILE_METADATA);

    return new Promise((resolve, reject) => {
      const request = store.get(metadataId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Update file metadata
  async updateFileMetadata(metadataId, updates) {
    const metadata = await this.getFileMetadataById(metadataId);
    if (!metadata) {
      throw new Error('File metadata not found');
    }

    const updatedMetadata = {
      ...metadata,
      ...updates,
      updatedAt: new Date()
    };

    const tx = await this.transaction([STORES.FILE_METADATA], 'readwrite');
    const store = tx.objectStore(STORES.FILE_METADATA);

    return new Promise((resolve, reject) => {
      const request = store.put(updatedMetadata);
      request.onsuccess = () => resolve(updatedMetadata);
      request.onerror = () => reject(request.error);
    });
  }

  // Update or create file metadata (upsert)
  async upsertFileMetadata(fileId, metadataType, data) {
    try {
      const existing = await this.getFileMetadata(fileId, metadataType);

      if (existing) {
        return this.updateFileMetadata(existing.id, data);
      } else {
        return this.createFileMetadata({
          fileId,
          metadataType,
          ...data
        });
      }
    } catch (error) {
      // If creation fails due to constraint violation, try to fetch and update
      if (error.name === 'ConstraintError') {
        console.warn(`Constraint error for ${fileId}:${metadataType}, retrying with update...`);
        const existing = await this.getFileMetadata(fileId, metadataType);
        if (existing) {
          return this.updateFileMetadata(existing.id, data);
        }
      }
      throw error;
    }
  }

  // Delete file metadata
  async deleteFileMetadata(metadataId) {
    const tx = await this.transaction([STORES.FILE_METADATA], 'readwrite');
    const store = tx.objectStore(STORES.FILE_METADATA);

    return new Promise((resolve, reject) => {
      const request = store.delete(metadataId);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // List all metadata for a file
  async listFileMetadata(fileId) {
    const tx = await this.transaction([STORES.FILE_METADATA]);
    const store = tx.objectStore(STORES.FILE_METADATA);
    const index = store.index('fileId');

    return new Promise((resolve, reject) => {
      const metadataList = [];
      const request = index.openCursor(fileId);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          metadataList.push(cursor.value);
          cursor.continue();
        } else {
          // Sort by metadataType for consistent ordering
          metadataList.sort((a, b) => a.metadataType.localeCompare(b.metadataType));
          resolve(metadataList);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // ========================================================================
  // CHECKPOINT METHODS
  // ========================================================================

  /**
   * Flag a version-history entry (git commit) as a checkpoint, locally.
   * `checkpoint` shape: { id, file_id, commit_sha, label, created_at, synced }
   */
  async addCheckpoint(checkpoint) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.CHECKPOINTS], 'readwrite');
      const store = transaction.objectStore(STORES.CHECKPOINTS);
      const request = store.put(checkpoint);

      request.onsuccess = () => resolve(checkpoint);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all checkpoints for a file
   */
  async getCheckpoints(fileId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.CHECKPOINTS], 'readonly');
      const store = transaction.objectStore(STORES.CHECKPOINTS);
      const index = store.index('file_id');
      const request = index.getAll(fileId);

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Remove a checkpoint flag (by its local id)
   */
  async removeCheckpoint(checkpointId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.CHECKPOINTS], 'readwrite');
      const store = transaction.objectStore(STORES.CHECKPOINTS);
      const request = store.delete(checkpointId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get checkpoints created locally that haven't synced to the server yet
   */
  async getCheckpointsNeedingSync() {
    if (!this.db) await this.init();

    return new Promise((resolve) => {
      try {
        const transaction = this.db.transaction([STORES.CHECKPOINTS], 'readonly');
        const store = transaction.objectStore(STORES.CHECKPOINTS);
        const index = store.index('synced');
        const keyRange = IDBKeyRange.only(0); // 0 represents false for synced
        const request = index.getAll(keyRange);

        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => resolve([]);
      } catch (error) {
        console.error('Error in getCheckpointsNeedingSync:', error);
        resolve([]);
      }
    });
  }

  /**
   * Mark a locally-created checkpoint as synced to the server
   */
  async markCheckpointSynced(checkpointId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.CHECKPOINTS], 'readwrite');
      const store = transaction.objectStore(STORES.CHECKPOINTS);
      const getRequest = store.get(checkpointId);

      getRequest.onsuccess = () => {
        const checkpoint = getRequest.result;
        if (!checkpoint) return resolve();
        checkpoint.synced = true;
        const putRequest = store.put(checkpoint);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // ========================================================================
  // VERSION MERGE INFO METHODS
  // ========================================================================

  /**
   * Tag a commit as auto-merged/manually-resolved, locally.
   * `mergeInfo` shape: { id, file_id, commit_sha, language_code, merge_type, connected_users, created_at, synced }
   */
  async addMergeInfo(mergeInfo) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.VERSION_MERGE_INFO], 'readwrite');
      const store = transaction.objectStore(STORES.VERSION_MERGE_INFO);
      const request = store.put(mergeInfo);

      request.onsuccess = () => resolve(mergeInfo);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all merge-info tags for a file (optionally filtered by language).
   */
  async getMergeInfo(fileId, languageCode = null) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.VERSION_MERGE_INFO], 'readonly');
      const store = transaction.objectStore(STORES.VERSION_MERGE_INFO);
      const index = store.index('file_id');
      const request = index.getAll(fileId);

      request.onsuccess = () => {
        const results = request.result || [];
        resolve(languageCode ? results.filter(r => r.language_code === languageCode) : results);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get merge-info tags created locally that haven't synced to the server yet
   */
  async getMergeInfoNeedingSync() {
    if (!this.db) await this.init();

    return new Promise((resolve) => {
      try {
        const transaction = this.db.transaction([STORES.VERSION_MERGE_INFO], 'readonly');
        const store = transaction.objectStore(STORES.VERSION_MERGE_INFO);
        const index = store.index('synced');
        const keyRange = IDBKeyRange.only(0); // 0 represents false for synced
        const request = index.getAll(keyRange);

        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => resolve([]);
      } catch (error) {
        console.error('Error in getMergeInfoNeedingSync:', error);
        resolve([]);
      }
    });
  }

  /**
   * Mark a locally-created merge-info tag as synced to the server
   */
  async markMergeInfoSynced(mergeInfoId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.VERSION_MERGE_INFO], 'readwrite');
      const store = transaction.objectStore(STORES.VERSION_MERGE_INFO);
      const getRequest = store.get(mergeInfoId);

      getRequest.onsuccess = () => {
        const mergeInfo = getRequest.result;
        if (!mergeInfo) return resolve();
        mergeInfo.synced = true;
        const putRequest = store.put(mergeInfo);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // ========================================================================
  // RADIO PLAY RECORDINGS METHODS
  // ========================================================================

  /**
   * Save a completed "Convert to Audio" generation's metadata (not the audio
   * itself -- audio_url already points at a permanent public Spaces CDN URL,
   * see spaces_service.py). `recording` shape: { id, file_id, file_name,
   * audio_url, duration_seconds, character_count, line_count,
   * synthesized_count, skipped_count, created_at }.
   */
  async saveRadioPlayRecording(recording) {
    if (!this.db) await this.init();

    const entry = { id: recording.id || this.generateId(), created_at: new Date(), ...recording };

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.RADIO_PLAY_RECORDINGS], 'readwrite');
      const store = transaction.objectStore(STORES.RADIO_PLAY_RECORDINGS);
      const request = store.put(entry);

      request.onsuccess = () => resolve(entry);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get every past recording for a file, most recent first.
   */
  async getRadioPlayRecordings(fileId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.RADIO_PLAY_RECORDINGS], 'readonly');
      const store = transaction.objectStore(STORES.RADIO_PLAY_RECORDINGS);
      const index = store.index('file_id');
      const request = index.getAll(fileId);

      request.onsuccess = () => {
        const results = request.result || [];
        results.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Remove a recording entry (by its local id) -- does not delete the
   * underlying Spaces object, only the local reference to it.
   */
  async deleteRadioPlayRecording(recordingId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.RADIO_PLAY_RECORDINGS], 'readwrite');
      const store = transaction.objectStore(STORES.RADIO_PLAY_RECORDINGS);
      const request = store.delete(recordingId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ========================================================================
  // VERSION HISTORY METHODS
  // ========================================================================

  /**
   * Save a file version
   */
  async saveFileVersion(versionData) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.FILE_VERSIONS], 'readwrite');
      const store = transaction.objectStore(STORES.FILE_VERSIONS);

      const request = store.add(versionData);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all versions for a file (in a specific language)
   */
  async getFileVersions(fileId, languageCode = 'en') {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.FILE_VERSIONS], 'readonly');
      const store = transaction.objectStore(STORES.FILE_VERSIONS);
      const index = store.index('file_id');
      const request = index.getAll(fileId);

      request.onsuccess = () => {
        const versions = request.result.filter(v => v.language_code === languageCode);
        resolve(versions);
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get a specific version
   */
  async getFileVersion(fileId, version, languageCode = 'en') {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.FILE_VERSIONS], 'readonly');
      const store = transaction.objectStore(STORES.FILE_VERSIONS);
      const index = store.index('file_version_lang');
      const request = index.get([fileId, version, languageCode]);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete a specific version
   */
  async deleteFileVersion(versionId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.FILE_VERSIONS], 'readwrite');
      const store = transaction.objectStore(STORES.FILE_VERSIONS);
      const request = store.delete(versionId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Mark a version for sync to server
   */
  async markVersionForSync(versionId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.FILE_VERSIONS], 'readwrite');
      const store = transaction.objectStore(STORES.FILE_VERSIONS);

      const getRequest = store.get(versionId);

      getRequest.onsuccess = () => {
        const version = getRequest.result;
        if (version) {
          version.needs_sync = true;
          const putRequest = store.put(version);

          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve(); // Version doesn't exist, nothing to mark
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Get versions that need syncing to server
   */
  async getVersionsNeedingSync() {
    if (!this.db) await this.init();

    return new Promise((resolve, _reject) => {
      try {
        const transaction = this.db.transaction([STORES.FILE_VERSIONS], 'readonly');
        const store = transaction.objectStore(STORES.FILE_VERSIONS);

        // Check if needs_sync index exists
        if (!store.indexNames.contains('needs_sync')) {
          console.warn('needs_sync index does not exist on FILE_VERSIONS store - returning empty array');
          resolve([]);
          return;
        }

        const index = store.index('needs_sync');
        // Use IDBKeyRange for boolean comparison instead of passing boolean directly
        const keyRange = IDBKeyRange.only(1); // 1 represents true for needs_sync
        const request = index.getAll(keyRange);

        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => {
          console.error('Error getting versions needing sync:', request.error);
          resolve([]); // Return empty array instead of rejecting
        };
      } catch (error) {
        console.error('Error in getVersionsNeedingSync:', error);
        resolve([]); // Return empty array on any error
      }
    });
  }

  /**
   * Clear sync flag after successful sync
   */
  async clearVersionSyncFlag(versionId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.FILE_VERSIONS], 'readwrite');
      const store = transaction.objectStore(STORES.FILE_VERSIONS);

      const getRequest = store.get(versionId);

      getRequest.onsuccess = () => {
        const version = getRequest.result;
        if (version) {
          version.needs_sync = false;
          const putRequest = store.put(version);

          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve();
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Delete all versions for a file (when file is deleted)
   */
  async deleteFileVersions(fileId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.FILE_VERSIONS], 'readwrite');
      const store = transaction.objectStore(STORES.FILE_VERSIONS);
      const index = store.index('file_id');
      const request = index.openCursor(IDBKeyRange.only(fileId));

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // LOCAL-ONLY FILE SUPPORT

  // Create a local-only file (storageMode: 'local', never synced to cloud)
  async createLocalFile(fileData) {
    return await this.createFile({
      ...fileData,
      storageMode: 'local'
    });
  }

  // Get all files where storageMode === 'local'
  async getLocalFiles() {
    const allFiles = await this.listFiles();
    return allFiles.filter(file => file.storageMode === 'local');
  }

  // Check if a file is local-only
  async isLocalFile(fileId) {
    const file = await this.getFile(fileId);
    return file ? file.storageMode === 'local' : false;
  }
}

// Create and export singleton instance
const screenwriterDB = new ScreenwriterDB();

export default screenwriterDB;
export {
  STORES,
  FILE_SCHEMA,
  FILE_LANGUAGE_SCHEMA,
  AI_PROCESSING_RESULT_SCHEMA,
  PROJECT_SCHEMA,
  DOCUMENT_NOTES_SCHEMA,
  DOCUMENT_OUTLINES_SCHEMA
};
